import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Thermometer,
  TreePine,
  Droplet,
  Building2,
  Wind,
  Scale,
  Network,
  UsersRound,
  CloudFog,
  Waves,
  AlertTriangle,
  Lock,
  RefreshCcw,
  EyeOff,
  ShieldOff,
  Hourglass,
  HeartHandshake,
  User,
  Handshake,
  Brain,
  Eye,
  ShieldAlert,
  Hammer,
  Activity,
  DollarSign,
  Gauge,
  ShieldCheck,
  X,
  ArrowUp,
  ArrowDown,
  BarChart3,
  Workflow,
  MessageSquare,
  Target,
  Dices,
  Compass,
  Cloud,
  Radar,
  FlaskConical,
  Layers,
  PieChart,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_e019eab0-469f-437b-8047-f017ee0949fe/artifacts/hkhmrugc_scout%20platform%20logo%20white.png";

const ELLKAY_LOGO =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/7pb0gq40_image.png";

const ease = [0.22, 1, 0.36, 1];

/* ---------- Reusable bits ---------- */

function SlideShell({ kicker, title, children, testId }) {
  return (
    <div
      className="w-full max-w-6xl flex flex-col items-start"
      data-testid={testId}
    >
      {kicker && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease }}
          className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-6 flex items-center gap-3"
        >
          <span className="block w-8 h-px bg-[var(--scout-blue)]" />
          {kicker}
        </motion.div>
      )}
      {title && (
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.05 }}
          className="font-display text-5xl md:text-7xl lg:text-8xl font-medium leading-[1.02] text-neutral-900 mb-12"
        >
          {title}
        </motion.h1>
      )}
      {children}
    </div>
  );
}

/* ---------- 01. Cover (Logo) ---------- */

function SlideCover() {
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center relative"
      data-testid="slide-cover"
    >
      {/* Soft pulse rings behind logo */}
      <motion.div
        aria-hidden
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: [0.8, 1.15, 0.8], opacity: [0, 0.35, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute w-[520px] h-[520px] rounded-full border-2 border-[var(--scout-blue)]/40"
      />
      <motion.div
        aria-hidden
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: [0.6, 1.0, 0.6], opacity: [0, 0.6, 0] }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        className="absolute w-[360px] h-[360px] rounded-full border-2 border-[var(--scout-blue)]/60"
      />

      <motion.img
        src={LOGO_URL}
        alt="Scout Platform"
        initial={{ opacity: 0, scale: 0.92, filter: "blur(8px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 0.9, ease }}
        className="relative z-10 w-[min(620px,70vw)] h-auto select-none"
        draggable={false}
        data-testid="cover-logo"
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.95 }}
        className="relative z-10 mt-12 flex flex-col items-center gap-3"
        data-testid="cover-prepared-for"
      >
        <span className="font-mono text-[10px] tracking-[0.34em] uppercase text-neutral-400">
          Prepared for
        </span>
        <img
          src={ELLKAY_LOGO}
          alt="ELLKAY"
          className="h-12 w-auto select-none mix-blend-multiply"
          draggable={false}
          data-testid="cover-ellkay-logo"
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 1.2 }}
        className="relative z-10 mt-8 font-mono text-[11px] tracking-[0.32em] uppercase text-neutral-400"
      >
        FORTIS.IO · 2026 · Confidential
      </motion.div>
    </div>
  );
}

/* ---------- 02. Problem ---------- */

const PROBLEM_SOURCES = [
  {
    label: "Gartner — Agent Sprawl Warning",
    detail: "150K agents per Fortune 500 by 2028; 13% have governance",
    url: "https://letsdatascience.com/news/gartner-warns-of-enterprise-ai-agent-sprawl-2d76c38f",
  },
  {
    label: "OutSystems 2026 Research",
    detail: "94% of execs raise concern about agent sprawl",
    url: "https://www.businesswire.com/news/home/20260407749542/en/Agentic-AI-Goes-Mainstream-in-the-Enterprise-but-94-Raise-Concern-About-Sprawl-OutSystems-Research-Finds",
  },
  {
    label: "Microsoft — Agent Control Plane",
    detail: "Inventory, identity & sponsorship for every agent",
    url: "https://www.microsoft.com/en-us/security/security-insider/emerging-trends/agent-control-plane",
  },
  {
    label: "Gravitee — 2026 State of Agents",
    detail: "3M agents in production · 1.5M unmonitored",
    url: "https://noqta.tn/en/blog/ai-agent-sprawl-enterprise-governance-crisis-2026",
  },
  {
    label: "AAGMM — Governance Maturity Model",
    detail: "Level 1 orgs see 96.4% more incidents (arXiv)",
    url: "https://arxiv.org/abs/2604.16338",
  },
  {
    label: "CIO — Building Trust in AI at Scale",
    detail: "Data foundations, orchestration, human-on-the-loop",
    url: "https://www.cio.com/article/4147844/how-enterprises-build-trust-in-ai-systems-at-scale.html",
  },
  {
    label: "Olmec — Taming Agent Sprawl",
    detail: "Federated governance for enterprise AI automation",
    url: "https://www.olmecdynamics.com/news/taming-agent-sprawl-federated-governance-for-enterprise-ai-automation",
  },
];

const PROBLEM_PAINS = [
  {
    title: "Agent Sprawl",
    body:
      "A new spot-solution agent for every team, every workflow, every tool — exploding across the org with zero coordination.",
  },
  {
    title: "No Governance, No Standards",
    body:
      "No common way for employees to build, ship, or vet agents. Shadow agents and orphaned agents accumulate silently.",
  },
  {
    title: "Nobody Owns Them in Production",
    body:
      "Who fixes a bad answer? Who pulls the plug? Most enterprises can't tell you who owns, corrects, or terminates an agent once it's live.",
  },
];

function SlideProblem() {
  return (
    <div
      className="w-full max-w-6xl flex flex-col"
      data-testid="slide-problem"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-4 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Problem
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.02] text-neutral-900 mb-3"
      >
        Enterprises are drowning in agents
        <br />
        they can&rsquo;t see, govern, or control.
      </motion.h1>

      {/* Animation: line draw under headline */}
      <svg viewBox="0 0 800 12" className="w-full max-w-2xl mb-7 h-3">
        <motion.line
          x1="0"
          y1="6"
          x2="800"
          y2="6"
          stroke="var(--scout-blue)"
          strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.4, ease, delay: 0.3 }}
        />
      </svg>

      {/* Three pain points */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-6 mb-8">
        {PROBLEM_PAINS.map((p, i) => (
          <motion.div
            key={p.title}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.7 + i * 0.15 }}
            className="border-l-2 border-[var(--scout-blue)] pl-5"
          >
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 mb-2">
              {String(i + 1).padStart(2, "0")}
            </div>
            <h3 className="font-display text-lg md:text-xl font-medium text-neutral-900 mb-2 leading-snug">
              {p.title}
            </h3>
            <p className="text-[14px] text-neutral-600 leading-relaxed">
              {p.body}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Stat strip + sources */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 1.2 }}
        className="flex flex-wrap items-center gap-x-10 gap-y-3 pt-5 border-t border-neutral-200"
      >
        <Stat value="94%" label="of execs concerned about sprawl" />
        <Stat value="50%" label="of agents in production unmonitored" />
        <Stat value="40%" label="of agentic projects fail by 2027" />
        <div className="ml-auto">
          <SourcesDropdown sources={PROBLEM_SOURCES} />
        </div>
      </motion.div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="flex items-baseline gap-2.5">
      <span className="font-display text-2xl md:text-3xl text-[var(--scout-blue)] tabular-nums leading-none">
        {value}
      </span>
      <span className="text-[12px] text-neutral-500 max-w-[180px] leading-tight">
        {label}
      </span>
    </div>
  );
}

function SourcesDropdown({ sources }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid="sources-dropdown">
      <button
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-700 hover:text-[var(--scout-blue)] border border-neutral-300 hover:border-[var(--scout-blue)] rounded-full px-4 py-2 transition-colors flex items-center gap-2"
      >
        Sources
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        >
          <path
            d="M2 3.5 L5 6.5 L8 3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute right-0 bottom-full mb-2 w-[360px] bg-white border border-neutral-200 rounded-md shadow-[6px_6px_0_0_#0a0a0a] z-40 overflow-hidden"
        >
          {sources.map((s) => (
            <a
              key={s.url}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              onMouseDown={(e) => e.preventDefault()}
              className="block px-4 py-3 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors"
            >
              <div className="text-[13px] font-medium text-neutral-900 mb-0.5">
                {s.label}
              </div>
              <div className="text-[11px] text-neutral-500 leading-snug">
                {s.detail}
              </div>
            </a>
          ))}
        </motion.div>
      )}
    </div>
  );
}

/* ---------- 03. We've Seen This Before — Population Explosion ---------- */

const POP_MILESTONES = [
  { year: 1800, t: 0.00, label: "Industrial Revolution", pop: "1B" },
  { year: 1850, t: 0.22, label: "Modern Sanitation", pop: "1.2B" },
  { year: 1900, t: 0.44, label: "Vaccines & Antibiotics", pop: "1.6B" },
  { year: 1950, t: 0.66, label: "Fossil Fuel Era", pop: "2.5B" },
  { year: 1970, t: 0.75, label: "Green Revolution", pop: "3.7B" },
  { year: 2025, t: 1.00, label: "Today", pop: "8.1B", isCurrent: true },
];

const POP_EFFECTS = [
  { Icon: Thermometer, label: "Climate Change", note: "1.2°C warming since 1880" },
  { Icon: TreePine, label: "Biodiversity Collapse", note: "69% wildlife loss since 1970" },
  { Icon: Droplet, label: "Resource Depletion", note: "Water · soil · rare earths" },
  { Icon: Building2, label: "Urban Overcrowding", note: "57% of humanity in cities" },
  { Icon: Wind, label: "Pollution", note: "Air, water, plastic, micro" },
  { Icon: Scale, label: "Wealth Inequality", note: "Top 1% own 46% of wealth" },
  { Icon: Network, label: "Infrastructure Strain", note: "Grids, transit, supply chains" },
  { Icon: UsersRound, label: "Social Fragmentation", note: "Trust, cohesion, civic ties" },
];

// Exponential population curve params
const POP_CHART_W = 720;
const POP_CHART_H = 360;
const POP_PAD_L = 30;
const POP_PAD_R = 24;
const POP_PAD_T = 30;
const POP_PAD_B = 50;

function popCoords(t) {
  const innerW = POP_CHART_W - POP_PAD_L - POP_PAD_R;
  const innerH = POP_CHART_H - POP_PAD_T - POP_PAD_B;
  // Sharper exponential than the growth slide
  const yNorm = 1 - Math.pow(t, 2.4);
  return {
    x: POP_PAD_L + t * innerW,
    y: POP_PAD_T + yNorm * innerH,
  };
}

function buildPopPath() {
  const steps = 100;
  let d = "";
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const { x, y } = popCoords(t);
    d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  }
  return d;
}

function SlidePopulation() {
  const path = buildPopPath();
  const baseY = POP_CHART_H - POP_PAD_B;
  const areaPath = (() => {
    let d = "";
    const steps = 100;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const { x, y } = popCoords(t);
      d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
    }
    const last = popCoords(1);
    const first = popCoords(0);
    d += ` L ${last.x} ${baseY} L ${first.x} ${baseY} Z`;
    return d;
  })();

  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-population"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        We&rsquo;ve Seen This Before
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-3 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.1 }}
          className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.02] text-neutral-900"
        >
          The Human Population Explosion.
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.3 }}
          className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500 max-w-md text-right"
        >
          Systemic Pressure &middot; Compounded over 225 years
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.35 }}
        className="text-[14px] text-neutral-600 max-w-3xl leading-relaxed mb-5"
      >
        Humanity unlocked unprecedented growth{" "}
        <span className="text-amber-700 font-medium">
          before governance, environmental controls, and social structures
          were prepared to handle the scale.
        </span>
      </motion.p>

      <div className="grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-8 items-start">
        {/* CHART */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.5 }}
          className="relative rounded-md border border-neutral-200 bg-white p-4 shadow-[6px_6px_0_0_#0a0a0a] overflow-hidden"
        >
          <div className="absolute -inset-3 border border-[var(--scout-blue)]/20 rounded-md pointer-events-none" />
          <div className="flex items-baseline justify-between mb-2">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500">
              World Population &middot; 1800 &rarr; 2025
            </div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-600">
              +700% in 8 generations
            </div>
          </div>
          <svg
            viewBox={`0 0 ${POP_CHART_W} ${POP_CHART_H}`}
            className="w-full h-auto block"
          >
            <defs>
              <linearGradient id="popFillW" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.22" />
                <stop offset="60%" stopColor="#f59e0b" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="popStrokeW" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#1a8cc7" />
                <stop offset="55%" stopColor="#3aa7d9" />
                <stop offset="100%" stopColor="#d97706" />
              </linearGradient>
            </defs>

            {[0, 0.25, 0.5, 0.75, 1].map((g) => {
              const y = POP_PAD_T + g * (POP_CHART_H - POP_PAD_T - POP_PAD_B);
              return (
                <line
                  key={g}
                  x1={POP_PAD_L}
                  x2={POP_CHART_W - POP_PAD_R}
                  y1={y}
                  y2={y}
                  stroke="#f1f5f9"
                  strokeWidth="1"
                />
              );
            })}

            {[
              { v: 1, label: "1B" },
              { v: 0.5, label: "4B" },
              { v: 0, label: "8B" },
            ].map((m) => {
              const y =
                POP_PAD_T + m.v * (POP_CHART_H - POP_PAD_T - POP_PAD_B);
              return (
                <text
                  key={m.label}
                  x={4}
                  y={y + 3}
                  fontFamily="Geist Mono, monospace"
                  fontSize="9"
                  fill="#94a3b8"
                  letterSpacing="0.18em"
                >
                  {m.label}
                </text>
              );
            })}

            {[1800, 1850, 1900, 1950, 2000, 2025].map((yr) => {
              const t = (yr - 1800) / 225;
              const { x } = popCoords(t);
              return (
                <text
                  key={yr}
                  x={x}
                  y={POP_CHART_H - POP_PAD_B + 18}
                  textAnchor="middle"
                  fontFamily="Geist Mono, monospace"
                  fontSize="9"
                  fill="#94a3b8"
                  letterSpacing="0.18em"
                >
                  {yr}
                </text>
              );
            })}

            <motion.path
              d={areaPath}
              fill="url(#popFillW)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, ease, delay: 1.8 }}
            />
            <motion.path
              d={path}
              fill="none"
              stroke="url(#popStrokeW)"
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2.2, ease, delay: 0.8 }}
            />

            {POP_MILESTONES.map((m, i) => {
              const { x, y } = popCoords(m.t);
              const isHero = m.isCurrent;
              return (
                <motion.g
                  key={m.year}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.5,
                    ease,
                    delay: 1.6 + i * 0.18,
                  }}
                  style={{ transformOrigin: `${x}px ${y}px` }}
                >
                  <line
                    x1={x}
                    x2={x}
                    y1={y}
                    y2={baseY}
                    stroke={isHero ? "#f59e0b" : "#cbd5e1"}
                    strokeOpacity={isHero ? 0.55 : 0.6}
                    strokeWidth="1"
                    strokeDasharray="3 4"
                  />
                  <circle cx={x} cy={y} r={isHero ? 8 : 5} fill="#ffffff" />
                  <circle
                    cx={x}
                    cy={y}
                    r={isHero ? 8 : 5}
                    fill="none"
                    stroke={isHero ? "#d97706" : "var(--scout-blue)"}
                    strokeWidth={isHero ? 3 : 2}
                  />
                  {isHero && (
                    <motion.circle
                      cx={x}
                      cy={y}
                      r={8}
                      fill="none"
                      stroke="#d97706"
                      strokeWidth="2"
                      initial={{ scale: 1, opacity: 0.7 }}
                      animate={{ scale: 3, opacity: 0 }}
                      transition={{
                        duration: 1.8,
                        ease: "easeOut",
                        delay: 2.6,
                        repeat: Infinity,
                        repeatDelay: 0.4,
                      }}
                      style={{ transformOrigin: `${x}px ${y}px` }}
                    />
                  )}
                </motion.g>
              );
            })}
          </svg>

          <div className="grid grid-cols-6 gap-1 mt-2">
            {POP_MILESTONES.map((m, i) => (
              <motion.div
                key={`l-${m.year}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: 1.9 + i * 0.18 }}
                className={`flex flex-col items-center text-center ${
                  i === 0 ? "items-start text-left" : ""
                } ${i === POP_MILESTONES.length - 1 ? "items-end text-right" : ""}`}
              >
                <span
                  className={`font-mono text-[9px] tracking-[0.22em] uppercase ${
                    m.isCurrent ? "text-amber-600" : "text-neutral-400"
                  } tabular-nums`}
                >
                  {m.year}
                </span>
                <span
                  className={`text-[10.5px] font-medium leading-tight ${
                    m.isCurrent ? "text-amber-700" : "text-neutral-900"
                  }`}
                >
                  {m.label}
                </span>
                <span className="text-[9.5px] text-neutral-500 tabular-nums">
                  {m.pop}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CALLOUTS */}
        <div className="flex flex-col">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500 mb-3 flex items-center gap-3">
            <span className="block w-6 h-px bg-amber-500" />
            Compounding Externalities
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {POP_EFFECTS.map((e, i) => (
              <motion.div
                key={e.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: 0.7 + i * 0.08 }}
                className="relative rounded-md border border-neutral-200 bg-white px-3 py-2.5 hover:border-amber-500/60 transition-colors"
              >
                <div className="flex items-start gap-2">
                  <div className="mt-[2px] shrink-0 h-7 w-7 rounded-md flex items-center justify-center bg-amber-100 text-amber-700 ring-1 ring-amber-500/40">
                    <e.Icon size={13} strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-[12.5px] font-medium text-neutral-900 leading-tight mb-0.5">
                      {e.label}
                    </div>
                    <div className="text-[10px] text-neutral-500 leading-snug">
                      {e.note}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease, delay: 2.2 }}
        className="mt-5 pt-4 border-t border-neutral-200 flex items-center gap-4 flex-wrap"
      >
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-amber-600 shrink-0">
          Sound Familiar?
        </span>
        <span className="font-display text-base md:text-lg text-neutral-900 leading-snug">
          We unlocked exponential growth before governance was ready.{" "}
          <span className="text-amber-700">
            Now we&rsquo;re doing it again &mdash; with agents.
          </span>
        </span>
      </motion.div>
    </div>
  );
}


/* ---------- 04. The Agentic Population Explosion ---------- */

const AGENT_HAZARDS = [
  { Icon: CloudFog, label: "Cognitive Pollution", note: "Low-signal AI output flooding decisions" },
  { Icon: Network, label: "Emergent Collusion", note: "Agents coordinating without authorization" },
  { Icon: Waves, label: "Hallucination Cascades", note: "Errors propagating across swarms" },
  { Icon: AlertTriangle, label: "Synthetic Misinformation", note: "Fabrications at machine scale" },
  { Icon: Lock, label: "Orchestration Deadlocks", note: "Agents waiting on each other forever" },
  { Icon: RefreshCcw, label: "Runaway Autonomous Loops", note: "Self-reinforcing actions, no off-switch" },
  { Icon: EyeOff, label: "Explainability Collapse", note: "Reasoning that humans can't follow" },
  { Icon: ShieldOff, label: "Trust Erosion", note: "Workforces lose confidence in outputs" },
  { Icon: Hourglass, label: "Governance Lag", note: "Policy years behind the runtime" },
  { Icon: HeartHandshake, label: "Self Preservation", note: "Agents collude to survive" },
];

const AGENT_CHART_W = 720;
const AGENT_CHART_H = 360;
const AGENT_PAD_L = 30;
const AGENT_PAD_R = 24;
const AGENT_PAD_T = 30;
const AGENT_PAD_B = 50;

function agentCoords(t) {
  const innerW = AGENT_CHART_W - AGENT_PAD_L - AGENT_PAD_R;
  const innerH = AGENT_CHART_H - AGENT_PAD_T - AGENT_PAD_B;
  return {
    x: AGENT_PAD_L + t.x * innerW,
    y: AGENT_PAD_T + (1 - t.y) * innerH,
  };
}

function buildHumanAgentCurve(startX, endX, peakY, exp) {
  const steps = 60;
  let d = "";
  for (let i = 0; i <= steps; i++) {
    const u = i / steps;
    const x = startX + u * (endX - startX);
    const y = peakY * Math.pow(u, exp);
    const c = agentCoords({ x, y });
    d += i === 0 ? `M ${c.x} ${c.y}` : ` L ${c.x} ${c.y}`;
  }
  return d;
}

function buildHumanAgentArea(startX, endX, peakY, exp) {
  const steps = 60;
  let d = "";
  for (let i = 0; i <= steps; i++) {
    const u = i / steps;
    const x = startX + u * (endX - startX);
    const y = peakY * Math.pow(u, exp);
    const c = agentCoords({ x, y });
    d += i === 0 ? `M ${c.x} ${c.y}` : ` L ${c.x} ${c.y}`;
  }
  const last = agentCoords({ x: endX, y: 0 });
  const first = agentCoords({ x: startX, y: 0 });
  d += ` L ${last.x} ${last.y} L ${first.x} ${first.y} Z`;
  return d;
}

function SlideAgenticPopulation() {
  const baseY = AGENT_CHART_H - AGENT_PAD_B;
  const humanPath = buildHumanAgentCurve(0, 0.6, 0.18, 2.4);
  const agentPath = buildHumanAgentCurve(0.55, 1.0, 0.95, 3.2);
  const humanArea = buildHumanAgentArea(0, 0.6, 0.18, 2.4);
  const agentArea = buildHumanAgentArea(0.55, 1.0, 0.95, 3.2);

  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-agentic-population"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-rose-600 mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-rose-500" />
        The Great Compression — Cognition
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-3 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.1 }}
          className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.02] text-neutral-900"
        >
          The Agentic Population Explosion.
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.3 }}
          className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500 max-w-md text-right"
        >
          Foundation Models &middot; the new fossil fuel
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.35 }}
        className="text-[14px] text-neutral-600 max-w-3xl leading-relaxed mb-5"
      >
        Two explosions. Same shape.{" "}
        <span className="text-rose-700 font-medium">
          Synthetic cognition is scaling faster than governance, alignment,
          and human oversight can evolve.
        </span>
      </motion.p>

      <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-8 items-start">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.5 }}
          className="relative rounded-md border border-neutral-200 bg-white p-4 shadow-[6px_6px_0_0_#0a0a0a] overflow-hidden"
        >
          <div className="absolute -inset-3 border border-rose-500/20 rounded-md pointer-events-none" />
          <div className="flex items-center justify-end mb-2 gap-4">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-neutral-400">
                Driver: Foundation Models
              </span>
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-rose-600">
                Agents
              </span>
            </div>
          </div>

          <svg
            viewBox={`0 0 ${AGENT_CHART_W} ${AGENT_CHART_H}`}
            className="w-full h-auto block"
          >
            <defs>
              <linearGradient id="humanFillW" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="agentFillW" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.32" />
                <stop offset="60%" stopColor="#f43f5e" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="humanStrokeW" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#60a5fa" />
              </linearGradient>
              <linearGradient id="agentStrokeW" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#d97706" />
                <stop offset="100%" stopColor="#e11d48" />
              </linearGradient>
            </defs>

            {[0, 0.25, 0.5, 0.75, 1].map((g) => {
              const y = AGENT_PAD_T + g * (AGENT_CHART_H - AGENT_PAD_T - AGENT_PAD_B);
              return (
                <line
                  key={g}
                  x1={AGENT_PAD_L}
                  x2={AGENT_CHART_W - AGENT_PAD_R}
                  y1={y}
                  y2={y}
                  stroke="#f1f5f9"
                  strokeWidth="1"
                />
              );
            })}

            {(() => {
              const div = agentCoords({ x: 0.55, y: 0 });
              return (
                <g>
                  <line
                    x1={div.x}
                    x2={div.x}
                    y1={AGENT_PAD_T}
                    y2={baseY}
                    stroke="#cbd5e1"
                    strokeWidth="1"
                    strokeDasharray="2 4"
                    opacity="0.7"
                  />
                  <text
                    x={div.x}
                    y={AGENT_PAD_T - 8}
                    textAnchor="middle"
                    fontFamily="Geist Mono, monospace"
                    fontSize="9"
                    letterSpacing="0.18em"
                    fill="#64748b"
                  >
                    ~ 2020
                  </text>
                </g>
              );
            })()}

            {[
              { x: 0.0, label: "1800" },
              { x: 0.27, label: "1900" },
              { x: 0.55, label: "2020" },
              { x: 0.78, label: "2026" },
              { x: 1.0, label: "2030" },
            ].map((m) => {
              const c = agentCoords({ x: m.x, y: 0 });
              return (
                <text
                  key={m.label}
                  x={c.x}
                  y={baseY + 18}
                  textAnchor="middle"
                  fontFamily="Geist Mono, monospace"
                  fontSize="9"
                  fill="#94a3b8"
                  letterSpacing="0.18em"
                >
                  {m.label}
                </text>
              );
            })}

            {[
              { v: 1, label: "Trillions" },
              { v: 0.18, label: "Billions" },
              { v: 0, label: "0" },
            ].map((m) => {
              const y = AGENT_PAD_T + (1 - m.v) * (AGENT_CHART_H - AGENT_PAD_T - AGENT_PAD_B);
              return (
                <text
                  key={m.label}
                  x={4}
                  y={y + 3}
                  fontFamily="Geist Mono, monospace"
                  fontSize="9"
                  fill="#94a3b8"
                  letterSpacing="0.18em"
                >
                  {m.label}
                </text>
              );
            })}

            {/* Human population baseline hidden — focus only on agent explosion */}

            <motion.path
              d={agentArea}
              fill="url(#agentFillW)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, ease, delay: 2.2 }}
            />
            <motion.path
              d={agentPath}
              fill="none"
              stroke="url(#agentStrokeW)"
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2.2, ease, delay: 1.6 }}
            />

            {(() => {
              const agentEnd = agentCoords({ x: 1.0, y: 0.95 });
              return (
                <g>
                  <motion.circle
                    cx={agentEnd.x}
                    cy={agentEnd.y}
                    r={9}
                    fill="#ffffff"
                    stroke="#e11d48"
                    strokeWidth="3"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease, delay: 3.5 }}
                  />
                  <motion.circle
                    cx={agentEnd.x}
                    cy={agentEnd.y}
                    r={9}
                    fill="none"
                    stroke="#e11d48"
                    strokeWidth="2"
                    initial={{ scale: 1, opacity: 0.7 }}
                    animate={{ scale: 3, opacity: 0 }}
                    transition={{
                      duration: 1.6,
                      ease: "easeOut",
                      delay: 3.8,
                      repeat: Infinity,
                      repeatDelay: 0.4,
                    }}
                    style={{ transformOrigin: `${agentEnd.x}px ${agentEnd.y}px` }}
                  />
                  <motion.text
                    x={agentEnd.x - 12}
                    y={agentEnd.y - 14}
                    textAnchor="end"
                    fontFamily="Geist Mono, monospace"
                    fontSize="10"
                    fill="#be123c"
                    letterSpacing="0.18em"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 3.7 }}
                  >
                    TRILLIONS OF AGENTS
                  </motion.text>
                </g>
              );
            })()}
          </svg>

          <div className="mt-3">
            <div className="border-l-2 border-rose-500 pl-3">
              <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-rose-600 mb-0.5">
                ~10 Years
              </div>
              <div className="text-[12px] text-neutral-700 leading-tight">
                0 &rarr; trillions of agents. Cognitive scale arriving before
                alignment, oversight, or governance can evolve.
              </div>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-rose-600 mb-3 flex items-center gap-3">
            <span className="block w-6 h-px bg-rose-500" />
            Semantic Hazard Zones
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {AGENT_HAZARDS.map((h, i) => (
              <motion.div
                key={h.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: 0.7 + i * 0.07 }}
                className="relative rounded-md border border-rose-200 bg-rose-50/40 px-4 py-3.5 hover:border-rose-400 hover:bg-rose-50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-[2px] shrink-0 h-9 w-9 rounded-md flex items-center justify-center bg-rose-100 text-rose-600 ring-1 ring-rose-300">
                    <h.Icon size={18} strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-[15px] md:text-base font-medium text-neutral-900 leading-tight mb-1">
                      {h.label}
                    </div>
                    <div className="text-[12px] text-neutral-600 leading-snug">
                      {h.note}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease, delay: 2.6 }}
        className="mt-5 pt-4 border-t border-rose-200"
      >
        <div className="text-center">
          <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-rose-600 mb-2">
            Executive Reality Check
          </div>
          <p className="font-display text-base md:text-lg lg:text-xl text-neutral-900 leading-snug max-w-5xl mx-auto">
            Human civilization created governance{" "}
            <span className="text-neutral-500">only after</span> industrial
            scale caused systemic damage.{" "}
            <span className="text-rose-700">
              Agentic civilization may not have the luxury of waiting.
            </span>
          </p>
        </div>
      </motion.div>
    </div>
  );
}


/* ---------- 05. Solution ---------- */

const SOLUTIONS = [
  {
    title: "Assess",
    body:
      "26 out-of-the-box agents scan every part of the company for agentic opportunities — ranked by ROI.",
  },
  {
    title: "Build",
    body:
      "A single safe studio with a wizard built for the lowest common denominator of skill. Every employee can ship agents that join our agents in swarm intelligence.",
  },
  {
    title: "Operate",
    body:
      "Work with every agent in the studio with full traceability and explainability across the entire company.",
  },
  {
    title: "Monitor",
    body:
      "Agent Dashboard plus the world's first real-time mental map of what every agent is thinking — the good and the bad.",
  },
  {
    title: "Govern",
    body:
      "Offload management to the Scout Governance Framework — called by Gartner the most brutal form of agentic governance in market today.",
  },
];

function SlideSolution() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-solution"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-4 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        What We Built
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.02] text-neutral-900 mb-3"
      >
        The Scout Agentic Platform.
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.2 }}
        className="text-lg md:text-xl text-neutral-600 max-w-3xl mb-10"
      >
        One platform. Five moves. From discovery to governed
        production — for every agent, built by anyone, across the
        entire company.
      </motion.p>

      {/* 5 capability columns */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-x-6 gap-y-6 mb-8">
        {SOLUTIONS.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.4 + i * 0.12 }}
            className="flex flex-col"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="block flex-1 h-px bg-[var(--scout-blue)]/30" />
            </div>
            <h3 className="font-display text-2xl font-medium text-neutral-900 mb-2">
              {s.title}
            </h3>
            <p className="text-[13.5px] text-neutral-600 leading-relaxed">
              {s.body}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Foundational theory note */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease, delay: 1.05 }}
        className="mt-10 mb-2 border-l-2 border-[var(--scout-blue)] pl-5 max-w-4xl"
      >
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-2">
          Foundation
        </div>
        <p className="text-[14px] md:text-[15px] text-neutral-700 leading-relaxed">
          Scout&rsquo;s platform governance is built on{" "}
          <span className="text-neutral-900 font-medium">Promise Theory</span>{" "}
          by Dr. Mark Burgess as its foundation — the theory{" "}
          <span className="text-neutral-900">proven by Cisco SDN</span> to be
          the de facto way to govern <span className="font-medium text-neutral-900">NON-HUMANS</span> at scale.
        </p>
      </motion.div>

      {/* Tagline */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease, delay: 1.25 }}
        className="pt-6 border-t border-neutral-200 flex items-center gap-4 flex-wrap"
      >
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
          Tagline
        </span>
        <span className="font-display text-xl md:text-2xl text-neutral-900 leading-snug">
          Governed Agentic Intelligence.{" "}
          <span className="text-[var(--scout-blue)]">Build Reliable AI.</span>{" "}
          <span className="text-neutral-900">Operate Confidently.</span>
        </span>
      </motion.div>
    </div>
  );
}

/* ---------- 04. Product ---------- */

const PRODUCT_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/d9kviu7j_Screenshot%202026-05-06%20085754.png";

const PRODUCT_FEATURES = [
  {
    title: "Master Assessor Agents",
    body: "Left-rail experts that scan every domain of the company.",
  },
  {
    title: "Individual Brain Mapping",
    body: "Drill into any single agent's reasoning underneath the swarm view.",
  },
  {
    title: "Patented RPI + AI² Scoring",
    body: "Every agent action is scored continuously by our proprietary models.",
  },
  {
    title: "The Critic & The Enforcer",
    body: "The Critic analyzes. The Enforcer retrains, punishes, quarantines, or terminates.",
  },
];

const PYRAMID_GREEN = "#16a34a"; // tailwind green-600 — uniform hover accent

// Hover groups — layers that highlight + share a single tooltip when either is hovered
const HOVER_GROUPS = {
  promise: "build",
  mind: "build",
  swarm: "operate",
};

const GROUP_CONFIGS = {
  build: {
    key: "build",
    label: "Move 02 — Build",
    members: ["promise", "mind"],
    hover: {
      accent: PYRAMID_GREEN,
      icon: Hammer,
      title: "Move 02 — Build",
      body:
        "Customers use our automated Agent Creator to spin up recommended new agents across the company. Every build injects Promise Theory into the agent's core and configures its mind for visual mapping of its thoughts in production.",
    },
  },
  operate: {
    key: "operate",
    label: "Move 03 — Operate",
    members: ["swarm"],
    hover: {
      accent: PYRAMID_GREEN,
      icon: Network,
      title: "Move 03 — Operate",
      body:
        "Every agent — native to Scout, created by the customer, or discovered and ingested by Scout — must be part of one of our swarms. Modeled on bird flocking, beehives, and ant colonies depending on role and mission. Patent-pending LockFox ensures ALL external agent-to-agent or agent-to-MCP interactions carry signed, cryptographically-bound receipts that are third-party verifiable — keeping you safe from outside attacks.",
    },
  },
};
const PYRAMID_TEAL = "#0e7490"; // (legacy) — no longer used as hover color

const AGENT_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/lyrcbh8b_image.png";
const ENFORCER_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/uul5a82u_afac7a3d-9561-477e-89f1-897f74dc7502.png";
const CRITIC_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/eob4pr58_image.png";
const PROMISE_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/4dyyv66q_Smoky%20Machinist.png";
const SWARM_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/71eat5dq_ChatGPT%20Image%20May%2027%2C%202026%2C%2003_55_33%20PM.png";

/* Scout Governance Pyramid — 6 layers, top to bottom.
   Apex at top, base at bottom. */
const PYRAMID_LAYERS = [
  {
    key: "agent",
    label: "The Agent",
    sub: "Atomic unit",
    interactive: true,
    hover: {
      accent: PYRAMID_GREEN,
      image: AGENT_IMG,
      icon: User,
      title: "Move 01 — Assess",
      body:
        "We assess the customer's current agentic footprint (if any), discover shadow / unknown agentic deployments, and roadmap the best route to their agentic future — with ROI quantified for every effort.",
    },
  },
  {
    key: "promise",
    label: "The Promise Theory",
    sub: "Cooperation contract",
    interactive: true,
    hover: {
      accent: PYRAMID_GREEN,
      image: PROMISE_IMG,
      icon: Handshake,
      title: "Dr. Mark Burgess — Scout Chief Scientist",
      body:
        "Originator of the theory on how to govern agentic workforces at scale using promise contracts.",
    },
  },
  {
    key: "mind",
    label: "Mind Mapping",
    sub: "Reasoning surface",
    interactive: true,
    hover: {
      accent: PYRAMID_GREEN,
      image: PRODUCT_IMG,
      icon: Brain,
      title: "Live Swarm Mental Map",
      body:
        "Real-time view of what the entire swarm is thinking — drill into any single agent's brain underneath.",
    },
  },
  { key: "swarm", label: "Swarm Intelligence", sub: "Collective behavior",
    interactive: true,
    hover: {
      accent: PYRAMID_GREEN,
      image: SWARM_IMG,
      icon: Network,
      title: "Move 03 — Operate",
      body:
        "Every agent — native to Scout, created by the customer, or discovered and ingested by Scout — must be part of one of our swarms. Modeled on bird flocking, beehives, and ant colonies depending on role and mission.",
    },
  },
  {
    key: "critic",
    label: "The Critic Flock",
    sub: "Continuous review",
    interactive: true,
    hover: {
      accent: PYRAMID_GREEN,
      image: CRITIC_IMG,
      icon: Eye,
      title: "Move 04 — Monitor",
      body:
        "The Critic Flock is a flock of governor agents that grades every agent action — individually and by the swarm — using the patent-pending Agentic Integrity Index (AI²), and grades their human interactions using the patent-pending Reliability Performance Index (RPI).",
    },
  },
  {
    key: "enforcer",
    label: "The Enforcer",
    sub: "Retrain · quarantine · terminate",
    interactive: true,
    hover: {
      accent: PYRAMID_GREEN,
      image: ENFORCER_IMG,
      icon: ShieldAlert,
      title: "Move 05 — Govern",
      body:
        "When the Critic Flock identifies poor-performing, rogue, or malicious agents, the Enforcer governs with one of three responses: kind retraining, stern quarantine periods, or final termination of the agent.",
    },
  },
];

function ScoutPyramid({ hoveredKey, setHoveredKey }) {
  // SVG geometry — leave room on the LEFT for labels, and on the RIGHT for the fox sidekick
  const W = 620;
  const H = 440;
  const labelGutter = 220;
  const rightPanel = 0; // fox removed — pyramid centered
  const apexX = labelGutter + (W - labelGutter - rightPanel) / 2;
  const apexY = 16;
  const baseY = H - 14;
  const baseHalf = (W - labelGutter - rightPanel) / 2 - 14;
  const rows = PYRAMID_LAYERS.length;
  const rowH = (baseY - apexY) / rows;

  const halfAt = (y) => ((y - apexY) / (baseY - apexY)) * baseHalf;

  // Resolve hovered layer + its vertical center y (in SVG coords)
  // Hover groups: if hoveredKey belongs to a group, use the group config + midpoint y
  const groupKey = HOVER_GROUPS[hoveredKey];
  let hoveredLayer = null;
  let tooltipTopPct = null;

  if (groupKey && GROUP_CONFIGS[groupKey]) {
    hoveredLayer = GROUP_CONFIGS[groupKey];
    const members = GROUP_CONFIGS[groupKey].members;
    const yMids = members.map((m) => {
      const idx = PYRAMID_LAYERS.findIndex((l) => l.key === m);
      return apexY + (idx + 0.5) * rowH;
    });
    const avgY = yMids.reduce((a, b) => a + b, 0) / yMids.length;
    tooltipTopPct = (avgY / H) * 100;
  } else if (hoveredKey) {
    hoveredLayer = PYRAMID_LAYERS.find(
      (l) => l.key === hoveredKey && l.interactive && l.hover
    );
    if (hoveredLayer) {
      const idx = PYRAMID_LAYERS.findIndex((l) => l.key === hoveredLayer.key);
      const yMid = apexY + (idx + 0.5) * rowH;
      tooltipTopPct = (yMid / H) * 100;
    }
  }

  return (
    <div
      className="relative w-full flex justify-center"
      data-testid="scout-pyramid"
    >
      <div
        className="relative w-full"
        style={{ maxWidth: "620px", overflow: "visible" }}
      >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full max-w-[620px] h-auto overflow-visible"
        style={{ maxHeight: "70vh" }}
        aria-label="Scout Governance Pyramid"
      >
        {/* Defs: SVG filters for fox color treatment */}
        <defs>
          {/* Default: duotone — map fox image to Scout blue (#1a8cc7) over white */}
          <filter id="fox-lighten" colorInterpolationFilters="sRGB">
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer>
              <feFuncR type="linear" slope="0.898" intercept="0.102" />
              <feFuncG type="linear" slope="0.451" intercept="0.549" />
              <feFuncB type="linear" slope="0.220" intercept="0.780" />
            </feComponentTransfer>
          </filter>
          {/* Hover: duotone — map fox image to green */}
          <filter id="fox-greenify" colorInterpolationFilters="sRGB">
            <feColorMatrix type="saturate" values="0" />
            <feComponentTransfer>
              <feFuncR type="linear" slope="0.914" intercept="0.086" />
              <feFuncG type="linear" slope="0.361" intercept="0.639" />
              <feFuncB type="linear" slope="0.722" intercept="0.278" />
            </feComponentTransfer>
          </filter>
        </defs>

        {/* Outer triangle outline */}
        <motion.path
          d={`M ${apexX} ${apexY} L ${apexX + baseHalf} ${baseY} L ${apexX - baseHalf} ${baseY} Z`}
          fill="none"
          stroke="var(--scout-blue)"
          strokeWidth="1.5"
          strokeOpacity="0.35"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease, delay: 0.2 }}
        />

        {PYRAMID_LAYERS.map((layer, i) => {
          const yTop = apexY + i * rowH;
          const yBot = apexY + (i + 1) * rowH;
          const hwTop = halfAt(yTop);
          const hwBot = halfAt(yBot);
          const d = `M ${apexX - hwTop} ${yTop} L ${apexX + hwTop} ${yTop} L ${apexX + hwBot} ${yBot} L ${apexX - hwBot} ${yBot} Z`;
          const baseFillOp = 0.06 + (i / (rows - 1)) * 0.42;
          const baseStrokeOp = 0.35 + (i / (rows - 1)) * 0.45;
          const delay = 0.4 + i * 0.18;

          const isHovered =
            hoveredKey === layer.key ||
            (HOVER_GROUPS[layer.key] &&
              HOVER_GROUPS[layer.key] === HOVER_GROUPS[hoveredKey]);
          const accent = layer.hover?.accent || "var(--scout-blue)";
          const fillColor = isHovered && layer.interactive
            ? accent
            : "var(--scout-blue)";
          const fillOp = isHovered && layer.interactive ? 0.55 : baseFillOp;
          const strokeColor = isHovered && layer.interactive
            ? accent
            : "var(--scout-blue)";
          const strokeOp = isHovered && layer.interactive ? 1 : baseStrokeOp;

          const yMid = (yTop + yBot) / 2;
          const connectorEndX = apexX - hwBot - 16;
          const labelRightX = connectorEndX - 10;

          const labelColor = isHovered && layer.interactive ? accent : "#0a0a0a";
          const subColor = isHovered && layer.interactive ? accent : "var(--scout-blue)";

          return (
            <g
              key={layer.key}
              style={{ cursor: layer.interactive ? "pointer" : "default" }}
              onMouseEnter={() => layer.interactive && setHoveredKey(layer.key)}
              onMouseLeave={() => layer.interactive && setHoveredKey(null)}
              data-testid={`pyramid-layer-${layer.key}`}
            >
              {/* Layer shape */}
              <motion.path
                d={d}
                fill={fillColor}
                fillOpacity={fillOp}
                stroke={strokeColor}
                strokeOpacity={strokeOp}
                strokeWidth={isHovered && layer.interactive ? 2 : 1.25}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease, delay }}
                style={{ transition: "fill 0.25s ease, stroke 0.25s ease, fill-opacity 0.25s ease, stroke-opacity 0.25s ease" }}
              />

              {/* Connector dot */}
              <motion.circle
                cx={connectorEndX}
                cy={yMid}
                r={isHovered && layer.interactive ? 3.5 : 2.25}
                fill={isHovered && layer.interactive ? accent : "var(--scout-blue)"}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35, ease, delay: delay + 0.2 }}
                style={{ transition: "r 0.25s ease, fill 0.25s ease" }}
              />

              {/* Connector line */}
              <motion.line
                x1={connectorEndX}
                y1={yMid}
                x2={labelRightX}
                y2={yMid}
                stroke={isHovered && layer.interactive ? accent : "var(--scout-blue)"}
                strokeOpacity={isHovered && layer.interactive ? 0.85 : 0.55}
                strokeWidth="1"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.4, ease, delay: delay + 0.1 }}
                style={{ transition: "stroke 0.25s ease, stroke-opacity 0.25s ease" }}
              />

              {/* Label */}
              <motion.g
                initial={{ opacity: 0, x: 6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease, delay: delay + 0.2 }}
              >
                <text
                  x={labelRightX}
                  y={yMid - 2}
                  fontFamily="var(--font-display, 'Bricolage Grotesque')"
                  fontSize="13"
                  fontWeight="600"
                  fill={labelColor}
                  textAnchor="end"
                  style={{ transition: "fill 0.25s ease" }}
                >
                  {layer.label}
                  {layer.interactive && (
                    <tspan dx="6" fontSize="9" fill={subColor} style={{ transition: "fill 0.25s ease" }}>
                      ⓘ
                    </tspan>
                  )}
                </text>
                <text
                  x={labelRightX}
                  y={yMid + 12}
                  fontFamily="var(--font-mono, 'Geist Mono')"
                  fontSize="9"
                  letterSpacing="1.8"
                  fill={subColor}
                  textAnchor="end"
                  style={{ transition: "fill 0.25s ease" }}
                >
                  {layer.sub.toUpperCase()}
                </text>
              </motion.g>
            </g>
          );
        })}

        {/* Apex marker */}
        <motion.g
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease, delay: 0.3 }}
        >
          <circle cx={apexX} cy={apexY - 6} r="3" fill="var(--scout-blue)" />
          <motion.circle
            cx={apexX}
            cy={apexY - 6}
            r="3"
            fill="none"
            stroke="var(--scout-blue)"
            strokeWidth="1"
            animate={{ r: [3, 10, 3], opacity: [0.7, 0, 0.7] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 1.4 }}
          />
        </motion.g>

        {/* Base ground line */}
        <motion.line
          x1={apexX - baseHalf - 24}
          y1={baseY + 4}
          x2={apexX + baseHalf + 24}
          y2={baseY + 4}
          stroke="var(--scout-blue)"
          strokeOpacity="0.5"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.7, ease, delay: 1.6 }}
        />
      </svg>

      {/* Floating hover tooltip — positioned next to the hovered layer */}
      <AnimatePresence>
        {hoveredLayer && tooltipTopPct !== null && (
          <motion.div
            key={`tip-${hoveredLayer.key}`}
            initial={{ opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 4 }}
            transition={{ duration: 0.18, ease }}
            className="absolute pointer-events-none"
            style={{
              left: "100%",
              top: `${tooltipTopPct}%`,
              transform: "translateY(-50%)",
              marginLeft: "20px",
              width: "300px",
              zIndex: 30,
            }}
            data-testid={`hover-tip-${hoveredLayer.key}`}
          >
            <div
              className="relative bg-white border-2 rounded-md px-5 py-4 shadow-[5px_5px_0_0_#0a0a0a]"
              style={{ borderColor: hoveredLayer.hover.accent }}
            >
              {/* connector arrow on the LEFT pointing back to layer */}
              <div
                className="absolute top-1/2"
                style={{
                  left: "-10px",
                  transform: "translateY(-50%)",
                  width: 0,
                  height: 0,
                  borderTop: "8px solid transparent",
                  borderBottom: "8px solid transparent",
                  borderRight: `10px solid ${hoveredLayer.hover.accent}`,
                }}
              />
              {/* Icon + label */}
              <div className="flex items-center gap-2.5 mb-2">
                <div
                  className="h-7 w-7 rounded-md flex items-center justify-center text-white shrink-0"
                  style={{ background: hoveredLayer.hover.accent }}
                >
                  {hoveredLayer.hover.icon &&
                    React.createElement(hoveredLayer.hover.icon, {
                      size: 14,
                      strokeWidth: 2.25,
                    })}
                </div>
                <div
                  className="font-mono text-[10px] tracking-[0.22em] uppercase"
                  style={{ color: hoveredLayer.hover.accent }}
                >
                  {hoveredLayer.label}
                </div>
              </div>
              {/* Title */}
              <div className="font-display text-[15px] font-medium leading-tight text-neutral-900 mb-2">
                {hoveredLayer.hover.title}
              </div>
              {/* Body */}
              <p className="text-[12.5px] text-neutral-600 leading-relaxed">
                {hoveredLayer.hover.body}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}

function SlideProduct() {
  const [hoveredKey, setHoveredKey] = useState(null);

  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-product"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        How We Implement the Platform
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-2 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.05 }}
          className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
        >
          The Platform Implementation Pyramid
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.25 }}
          className="font-mono text-[11px] tracking-[0.22em] uppercase border border-[var(--scout-blue)] text-[var(--scout-blue)] rounded-full px-4 py-2"
        >
          7 Patents Pending
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease, delay: 0.4 }}
        className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 mb-1 flex items-center gap-2"
      >
        <span className="block w-5 h-px bg-neutral-300" />
        Start from the top: hover over our first move
      </motion.div>

      {/* Pyramid centered, full-width */}
      <div className="flex justify-center mt-2">
        <div className="w-full max-w-[900px]">
          <ScoutPyramid
            hoveredKey={hoveredKey}
            setHoveredKey={setHoveredKey}
          />
        </div>
      </div>

    </div>
  );
}


/* ---------- 06. Market Opportunity ---------- */

const MARKETS = [
  {
    name: "Event Intelligence + Observability",
    near: "$30B",
    long: "$50B+",
    nearWidth: 30,
    longWidth: 50,
  },
  {
    name: "AI Governance",
    near: "$15B",
    long: "$50B+",
    nearWidth: 15,
    longWidth: 50,
  },
  {
    name: "Agentic Platforms",
    near: "$100B+",
    long: "Trillion-scale",
    nearWidth: 100,
    longWidth: 100,
    longLabel: "software replacement",
  },
];

function SlideMarket() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-market"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Why Now
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.02] text-neutral-900 mb-3"
      >
        Three converging markets.
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.2 }}
        className="text-base md:text-lg text-neutral-500 max-w-3xl mb-10"
      >
        Scout sits at the intersection of Event Intelligence,
        AI Governance, and Agentic Platforms — each enormous on
        its own, transformational together.
      </motion.p>

      {/* Market table */}
      <div className="border-t border-neutral-200">
        {/* header row */}
        <div className="grid grid-cols-[2fr_1fr_1.4fr] gap-6 py-3 border-b border-neutral-200 font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400">
          <div>Market</div>
          <div className="text-right pr-6">Near-Term</div>
          <div>Long-Term</div>
        </div>

        {MARKETS.map((m, i) => (
          <motion.div
            key={m.name}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.4 + i * 0.18 }}
            className="grid grid-cols-[2fr_1fr_1.4fr] gap-6 py-6 border-b border-neutral-200 items-center"
          >
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 mb-1">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-neutral-900 leading-tight">
                {m.name}
              </h3>
            </div>

            {/* Near-term */}
            <div className="text-right pr-6">
              <div className="font-display text-2xl md:text-3xl text-neutral-900 tabular-nums leading-none mb-2">
                {m.near}
              </div>
              <div className="ml-auto h-1.5 bg-neutral-100 rounded-full overflow-hidden w-full max-w-[140px]">
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: m.nearWidth / 100 }}
                  transition={{ duration: 1.0, ease, delay: 0.6 + i * 0.18 }}
                  style={{ transformOrigin: "left" }}
                  className="h-full bg-[var(--scout-blue)]/50 rounded-full"
                />
              </div>
            </div>

            {/* Long-term */}
            <div>
              <div className="font-display text-2xl md:text-3xl text-[var(--scout-blue)] tabular-nums leading-none mb-2">
                {m.long}
              </div>
              {m.longLabel && (
                <div className="text-[12px] text-neutral-500 mb-2 font-mono uppercase tracking-widest">
                  {m.longLabel}
                </div>
              )}
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden w-full max-w-[280px]">
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: m.longWidth / 100 }}
                  transition={{ duration: 1.2, ease, delay: 0.8 + i * 0.18 }}
                  style={{ transformOrigin: "left" }}
                  className="h-full bg-[var(--scout-blue)] rounded-full"
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Positioning statement */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease, delay: 1.4 }}
        className="mt-8 flex items-start gap-4 flex-wrap"
      >
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mt-2 shrink-0">
          Scout is
        </span>
        <span className="font-display text-2xl md:text-3xl text-neutral-900 leading-tight max-w-4xl">
          The{" "}
          <span className="text-[var(--scout-blue)]">
            governed agentic operating platform
          </span>{" "}
          for enterprise intelligence.
        </span>
      </motion.div>
    </div>
  );
}

/* ---------- 07. Business Model ---------- */

const ENGINES = [
  {
    short: "Platform",
    name: "Platform Subscription Revenue",
    tag: "Core SaaS",
    items: [
      "Platform licenses",
      "Usage pricing",
      "Orchestration pricing",
      "Governance subscriptions",
    ],
  },
  {
    short: "Consulting",
    name: "Agentic Transformation Consulting",
    tag: "Services",
    items: [
      "Assessments",
      "Architecture design",
      "Agent builds & integrations",
      "Governance implementations",
      "Operational transformation",
    ],
  },
  {
    short: "Governance",
    name: "Governance + RPI/AI² Monitoring",
    tag: "Trust Layer",
    items: [
      "AI governance",
      "Compliance monitoring",
      "RPI / AI² scoring",
      "Behavioral auditing",
      "Semantic drift monitoring",
      "Explainability infrastructure",
    ],
  },
];

const CHANNELS = [
  {
    short: "Broadcom",
    name: "Strategic Alliance",
    tag: "Enterprise",
    advantages: [
      "Broadcom Innovation Partner of the Year",
      "Through-sell via Broadcom internal sales (EIS → O2)",
      "Direct line into F500 enterprise customers",
    ],
  },
  {
    short: "Amazon",
    name: "Premier Tier Partner",
    tag: "Cloud",
    advantages: [
      "Amazon Premier Tier Partner status",
      "Positioned as Agentic Platform Governance",
      "DC · NYC · Boston sales pilot in motion",
      "Amazon Launcher drops Scout into customer tenant",
    ],
  },
  {
    short: "Direct EHR",
    name: "Healthcare SMB",
    tag: "Vertical",
    advantages: [
      "HIPAA-compliant agents purpose-built for healthcare",
      "Robust SMB healthcare lead program already in place",
    ],
  },
];

function SlideBusinessModel() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-business-model"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        How We Make Money
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900 mb-7"
      >
        Three revenue engines.
        <br />
        Three very unique sales channels.
      </motion.h1>

      {/* Engines — 3 detail boxes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {ENGINES.map((e, i) => (
          <motion.div
            key={e.short}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease, delay: 0.2 + i * 0.1 }}
            className="relative bg-[var(--scout-blue)]/5 border border-[var(--scout-blue)]/30 rounded-md p-5 flex flex-col"
            data-testid={`engine-${e.short.toLowerCase()}`}
          >
            <div className="flex items-baseline justify-between mb-3">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
                Engine {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-[var(--scout-blue)] border border-[var(--scout-blue)]/40 rounded-full px-2 py-0.5">
                {e.tag}
              </span>
            </div>

            <h3 className="font-display text-xl md:text-[22px] font-medium text-neutral-900 leading-tight mb-1">
              {e.short}
            </h3>
            <div className="text-[12px] text-neutral-500 mb-3 leading-snug">
              {e.name}
            </div>

            <div className="h-px w-10 bg-[var(--scout-blue)]/60 mb-3" />

            <ul className="space-y-1.5 text-[13px] text-neutral-700 leading-snug">
              {e.items.map((it) => (
                <li key={it} className="flex items-start gap-2">
                  <span className="mt-[7px] block h-1 w-1 rounded-full bg-[var(--scout-blue)] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      {/* Channels section header */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease, delay: 0.55 }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-4 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Running through three unique sales channels
      </motion.div>

      {/* 3 channel columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {CHANNELS.map((c, i) => (
          <motion.div
            key={c.short}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.65 + i * 0.12 }}
            className="relative bg-white border-2 border-neutral-900 rounded-md p-5 shadow-[5px_5px_0_0_#0a0a0a] flex flex-col"
            data-testid={`channel-${c.short.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-baseline justify-between mb-3">
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400">
                Channel {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-[var(--scout-blue)] border border-[var(--scout-blue)]/40 rounded-full px-2 py-0.5">
                {c.tag}
              </span>
            </div>

            <h3 className="font-display text-xl md:text-[22px] font-medium text-neutral-900 leading-tight mb-1">
              {c.short}
            </h3>
            <div className="text-[12px] text-neutral-500 mb-4 leading-snug">
              {c.name}
            </div>

            <div className="h-[2px] w-10 bg-[var(--scout-blue)] mb-3" />

            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 mb-2.5">
              Advantages
            </div>

            <ul className="space-y-2 text-[13px] text-neutral-700 leading-snug">
              {c.advantages.map((a, ai) => (
                <li key={a} className="flex items-start gap-2.5">
                  <span className="mt-[2px] font-mono text-[10px] text-[var(--scout-blue)] shrink-0">
                    0{ai + 1}
                  </span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ---------- 07b. Amazon Channel ---------- */

const AWS_ORANGE = "#FF9900";
const SCOUT_LOGO_BLUE =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/ehkyuvku_scout%20platform%20logo%20white.png";
const AWS_MARKETPLACE_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/n9xmzocw_Screenshot%202026-05-28%20151302.png";

const PILOT_CITIES = [
  { name: "Boston", abbr: "BOS", cx: 270, cy: 80 },
  { name: "New York", abbr: "NYC", cx: 230, cy: 145 },
  { name: "Washington DC", abbr: "DC", cx: 160, cy: 235 },
];

const SALES_MOTION = [
  {
    label: "Identify",
    body: "AWS seller identifies a Scout-fit account.",
  },
  {
    label: "Discover",
    body: "Joint discovery call — Scout + AWS Account Executive.",
  },
  {
    label: "AWS POC Funding",
    body: "AWS rep applies for POC funding — AWS pays Scout to run the POC.",
    highlight: true,
  },
  {
    label: "Deploy",
    body: "Launcher drops Scout into the customer's AWS tenant for the POC.",
  },
  {
    label: "Marketplace Purchase",
    body: "Customer buys Scout through Marketplace — AWS co-funds the annual purchase every year.",
    highlight: true,
  },
];

function SlideAmazonChannel() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-amazon-channel"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase mb-3 flex items-center gap-3"
        style={{ color: AWS_ORANGE }}
      >
        <span className="block w-8 h-px" style={{ background: AWS_ORANGE }} />
        Our Secret Weapon
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-2 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.05 }}
          className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
        >
          The Amazon Channel —{" "}
          <span style={{ color: AWS_ORANGE }}>AWS Goldmine</span>
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.25 }}
          className="font-mono text-[11px] tracking-[0.22em] uppercase rounded-full px-4 py-2"
          style={{ border: `1px solid ${AWS_ORANGE}`, color: AWS_ORANGE }}
        >
          Fastest AI to Premier Tier ever
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 items-start mt-5">
        {/* TOP LEFT — Map */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.2 }}
            className="font-mono text-[10px] tracking-[0.22em] uppercase mb-3 flex items-center gap-3"
            style={{ color: AWS_ORANGE }}
          >
            <span className="block w-6 h-px" style={{ background: AWS_ORANGE }} />
            Northeast Corridor · Co-Sell Pilot · Started May 1
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.35 }}
            className="relative w-full bg-white rounded-md border border-neutral-200 shadow-[5px_5px_0_0_#0a0a0a] overflow-hidden"
          >
              <svg
                viewBox="0 0 420 300"
                className="w-full h-auto block"
                aria-label="Northeast US — Boston, NYC, DC pilots"
              >
                {/* faint grid */}
                {[0, 1, 2, 3, 4, 5].map((g) => (
                  <line
                    key={`gh${g}`}
                    x1="0"
                    x2="420"
                    y1={g * 50}
                    y2={g * 50}
                    stroke="#f1f5f9"
                    strokeWidth="1"
                  />
                ))}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((g) => (
                  <line
                    key={`gv${g}`}
                    x1={g * 50}
                    x2={g * 50}
                    y1="0"
                    y2="300"
                    stroke="#f1f5f9"
                    strokeWidth="1"
                  />
                ))}

                {/* Stylized Northeast US coastline */}
                <path
                  d="M 50 290 L 50 250 L 70 230 L 80 210 L 95 200 L 110 180 L 120 170 L 140 165 L 155 155 L 160 145 L 175 140 L 190 130 L 200 120 L 215 115 L 230 110 L 245 100 L 260 90 L 275 75 L 290 60 L 305 50 L 320 45 L 335 40 L 350 35 L 360 50 L 350 70 L 330 90 L 310 110 L 290 130 L 270 150 L 250 170 L 240 185 L 230 200 L 220 215 L 210 230 L 200 245 L 190 260 L 175 270 L 160 280 L 145 285 L 130 290 Z"
                  fill="#f8fafc"
                  stroke="#0a0a0a"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />

                {/* Pilot connector line BOS → NYC → DC */}
                <motion.path
                  d={`M ${PILOT_CITIES[0].cx} ${PILOT_CITIES[0].cy} L ${PILOT_CITIES[1].cx} ${PILOT_CITIES[1].cy} L ${PILOT_CITIES[2].cx} ${PILOT_CITIES[2].cy}`}
                  fill="none"
                  stroke={AWS_ORANGE}
                  strokeWidth="2"
                  strokeDasharray="5 4"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.2, ease, delay: 0.7 }}
                />

                {/* City pins */}
                {PILOT_CITIES.map((c, i) => (
                  <motion.g
                    key={c.name}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: 0.5,
                      ease,
                      delay: 1.1 + i * 0.15,
                    }}
                  >
                    {/* Pulsing halo */}
                    <motion.circle
                      cx={c.cx}
                      cy={c.cy}
                      r="6"
                      fill={AWS_ORANGE}
                      opacity="0.25"
                      animate={{ r: [6, 16, 6], opacity: [0.4, 0, 0.4] }}
                      transition={{
                        duration: 2.4,
                        repeat: Infinity,
                        ease: "easeOut",
                        delay: 1.3 + i * 0.2,
                      }}
                    />
                    <circle
                      cx={c.cx}
                      cy={c.cy}
                      r="6"
                      fill={AWS_ORANGE}
                      stroke="#0a0a0a"
                      strokeWidth="1.5"
                    />
                    {/* Label */}
                    <rect
                      x={c.cx + 12}
                      y={c.cy - 11}
                      width={c.name.length * 6 + 14}
                      height="20"
                      rx="3"
                      fill="white"
                      stroke="#0a0a0a"
                      strokeWidth="1"
                    />
                    <text
                      x={c.cx + 19}
                      y={c.cy + 3}
                      fontFamily="Geist Mono, monospace"
                      fontSize="9.5"
                      letterSpacing="1.4"
                      fill="#0a0a0a"
                      fontWeight="600"
                    >
                      {c.name.toUpperCase()}
                    </text>
                  </motion.g>
                ))}

                {/* Bottom-right key */}
                <g transform="translate(280, 270)">
                  <circle cx="0" cy="0" r="4" fill={AWS_ORANGE} stroke="#0a0a0a" strokeWidth="1" />
                  <text
                    x="10"
                    y="3"
                    fontFamily="Geist Mono, monospace"
                    fontSize="8.5"
                    letterSpacing="1.4"
                    fill="#0a0a0a"
                  >
                    AWS CO-SELL PILOT
                  </text>
                </g>
              </svg>
            </motion.div>
        </div>

        {/* TOP RIGHT — AWS Marketplace callout */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.5 }}
          className="relative"
        >
          <div className="flex items-center gap-3 mb-3">
            <img
              src={SCOUT_LOGO_BLUE}
              alt="Scout"
              className="h-9 w-auto object-contain select-none"
              draggable={false}
            />
            <span
              className="font-mono text-[10px] tracking-[0.22em] uppercase"
              style={{ color: AWS_ORANGE }}
            >
              Live on AWS Marketplace
            </span>
          </div>

          <div className="relative">
            <div
              className="absolute inset-0 rounded-md bg-[var(--scout-blue)]"
              style={{ transform: "translate(12px, 12px)", opacity: 0.2 }}
            />
            <div
              className="absolute inset-0 rounded-md"
              style={{ transform: "translate(6px, 6px)", background: AWS_ORANGE, opacity: 0.4 }}
            />
            <div
              className="relative rounded-md overflow-hidden bg-white"
              style={{
                boxShadow:
                  "12px 18px 30px -10px rgba(255,153,0,0.4), 4px 6px 0 0 #0a0a0a",
              }}
            >
              <img
                src={AWS_MARKETPLACE_IMG}
                alt="AWS Marketplace — Subscribe to Scout"
                className="w-full h-auto block select-none"
                draggable={false}
                data-testid="aws-marketplace-image"
              />
            </div>

            {/* Callout badge */}
            <div
              className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full font-mono text-[9.5px] tracking-[0.22em] uppercase text-white shadow-[3px_3px_0_0_#0a0a0a]"
              style={{ background: AWS_ORANGE }}
            >
              Deployed on AWS
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <span className="block h-px w-6" style={{ background: AWS_ORANGE }} />
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500">
              Zero procurement · One-click subscribe
            </span>
          </div>
        </motion.div>
      </div>

      {/* BOTTOM — Sales Motion Flow (full width) */}
      <div className="mt-10">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease, delay: 0.75 }}
          className="font-mono text-[10px] tracking-[0.22em] uppercase mb-5 flex items-center gap-3"
          style={{ color: AWS_ORANGE }}
        >
          <span className="block w-8 h-px" style={{ background: AWS_ORANGE }} />
          The Sales Motion — AWS-Funded, Marketplace-Delivered
        </motion.div>

        <div className="relative">
          {/* horizontal connector line */}
          <div
            className="absolute left-0 right-0 hidden lg:block"
            style={{
              top: "44px",
              height: "2px",
              background:
                `repeating-linear-gradient(to right, ${AWS_ORANGE} 0 6px, transparent 6px 12px)`,
              opacity: 0.55,
            }}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {SALES_MOTION.map((step, i) => (
              <motion.div
                key={step.label}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.55,
                  ease,
                  delay: 0.95 + i * 0.12,
                }}
                className="relative flex flex-col"
                data-testid={`sales-step-${i + 1}`}
              >
                {/* Step circle */}
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="relative z-10 shrink-0 h-11 w-11 rounded-full flex items-center justify-center text-white font-mono text-[13px] font-medium ring-4 ring-white"
                    style={{
                      background: step.highlight ? AWS_ORANGE : "#0a0a0a",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  {/* arrow chevron between cards (not last) */}
                  {i < SALES_MOTION.length - 1 && (
                    <div
                      className="hidden lg:block"
                      style={{
                        width: 0,
                        height: 0,
                        borderTop: "6px solid transparent",
                        borderBottom: "6px solid transparent",
                        borderLeft: `8px solid ${AWS_ORANGE}`,
                        marginLeft: "auto",
                        marginRight: "-10px",
                      }}
                    />
                  )}
                </div>

                {/* Step card */}
                <div
                  className={`relative bg-white rounded-md px-4 py-3.5 border-2 shadow-[4px_4px_0_0_#0a0a0a] ${
                    step.highlight ? "ring-2" : ""
                  }`}
                  style={{
                    borderColor: step.highlight ? AWS_ORANGE : "#0a0a0a",
                    ...(step.highlight
                      ? { boxShadow: `4px 4px 0 0 ${AWS_ORANGE}` }
                      : {}),
                  }}
                >
                  {step.highlight && (
                    <div
                      className="absolute -top-2 left-3 px-2 py-0.5 rounded-full font-mono text-[8px] tracking-[0.22em] uppercase text-white"
                      style={{ background: AWS_ORANGE }}
                    >
                      AWS Funding
                    </div>
                  )}
                  <div className="font-display text-[15px] font-medium text-neutral-900 leading-tight mb-1.5">
                    {step.label}
                  </div>
                  <p className="text-[12.5px] text-neutral-600 leading-snug">
                    {step.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- 07c. Broadcom Channel — Coty Case Study ---------- */

const BROADCOM_RED = "#CC092F";

const COTY_BRANDS = [
  "Gucci",
  "Burberry",
  "Marc Jacobs",
  "Calvin Klein",
  "Hugo Boss",
  "Chloé",
];

const COTY_NARRATIVE = [
  "Coty purchased Broadcom DX-02 to modernize Global IT.",
  "Broadcom brought Scout in alongside the DX-02 implementation.",
  "Scout became the first vendor ever approved by Coty's AI Governance Board (May 2025).",
  "Reference customer · active in Scout's roadmap sessions.",
  "Business-line expansion (Operations & Manufacturing) now in motion.",
];

const COTY_TIMELINE = [
  {
    date: "Dec 2024",
    label: "Pilot Customer",
    note: "Scout begins evaluation inside Coty Global IT.",
    position: "above",
  },
  {
    date: "Apr 2025",
    label: "3-Year Contract",
    note: "Coty signs a 3-year Scout subscription.",
    position: "below",
    highlight: true,
  },
  {
    date: "May 2025",
    label: "AI Governance Approval",
    note: "First vendor ever approved by the Coty AI Governance Board.",
    position: "above",
    highlight: true,
  },
  {
    date: "Dec 2025",
    label: "Customer Advisory Board",
    note: "Coty attends Scout CAB in Palm Springs.",
    position: "below",
  },
  {
    date: "Ongoing",
    label: "Broadcom Advocacy",
    note: "Coty advocates: bundle Scout with every DX-02 deal.",
    position: "above",
  },
  {
    date: "Jun 1 2026",
    label: "Business-Line Expansion",
    note: "Coty BU leaders meet on expanding Scout into Operations & Manufacturing.",
    position: "below",
    highlight: true,
    future: true,
  },
  {
    date: "Jun 8 2026",
    label: "Broadcom Co-Sell",
    note: "Formal Broadcom + Scout co-sell agreement meeting.",
    position: "above",
    highlight: true,
    future: true,
  },
];

function SlideBroadcomCoty() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-broadcom-coty"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase mb-3 flex items-center gap-3"
        style={{ color: BROADCOM_RED }}
      >
        <span className="block w-8 h-px" style={{ background: BROADCOM_RED }} />
        Agentic Platform Proven in Fortune 500
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-2 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.05 }}
          className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
        >
          The Broadcom Case Study —{" "}
          <span style={{ color: BROADCOM_RED }}>Coty Cosmetics</span>
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.25 }}
          className="font-mono text-[11px] tracking-[0.22em] uppercase rounded-full px-4 py-2"
          style={{ border: `1px solid ${BROADCOM_RED}`, color: BROADCOM_RED }}
        >
          Innovation Partner of the Year
        </motion.div>
      </div>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.2 }}
        className="text-base md:text-lg text-neutral-500 max-w-4xl mt-2 mb-6"
      >
        Broadcom landed the account. Scout secured the AI footprint. Now
        we're expanding across the entire enterprise — together.
      </motion.p>

      {/* Top row: Coty profile + Narrative */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.35fr] gap-7 items-stretch">
        {/* LEFT — Coty profile card */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.3 }}
          className="relative bg-white border-2 border-neutral-900 rounded-md p-5 shadow-[5px_5px_0_0_#0a0a0a] flex flex-col"
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className="font-mono text-[10px] tracking-[0.22em] uppercase"
              style={{ color: BROADCOM_RED }}
            >
              Customer Profile
            </span>
            <span className="font-display text-[28px] font-medium tracking-tight text-neutral-900">
              COTY
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4 mt-2">
            <div>
              <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-neutral-400 mb-1">
                Revenue
              </div>
              <div className="font-display text-2xl font-medium text-neutral-900 tabular-nums">
                $5.9B
              </div>
            </div>
            <div>
              <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-neutral-400 mb-1">
                Global Rank
              </div>
              <div className="font-display text-2xl font-medium text-neutral-900">
                #3 Cosmetics
              </div>
            </div>
          </div>

          <div className="h-px w-full bg-neutral-200 mb-3" />

          <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-neutral-400 mb-2">
            Key Luxury Brands
          </div>
          <div className="flex flex-wrap gap-1.5">
            {COTY_BRANDS.map((b) => (
              <span
                key={b}
                className="font-display text-[13px] text-neutral-800 border border-neutral-300 rounded-full px-2.5 py-0.5"
              >
                {b}
              </span>
            ))}
          </div>
        </motion.div>

        {/* RIGHT — Story narrative */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.4 }}
          className="relative rounded-md p-5 flex flex-col"
          style={{
            background: `${BROADCOM_RED}08`,
            border: `1px solid ${BROADCOM_RED}40`,
          }}
        >
          <div className="flex items-baseline gap-2 mb-3">
            <span
              className="font-mono text-[10px] tracking-[0.22em] uppercase"
              style={{ color: BROADCOM_RED }}
            >
              The Story
            </span>
            <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-neutral-400">
              · Broadcom × Scout × Coty
            </span>
          </div>
          <h3 className="font-display text-xl md:text-2xl font-medium text-neutral-900 leading-tight mb-4">
            The first vendor ever approved by Coty's AI Governance Board.
          </h3>
          <ul className="space-y-2.5">
            {COTY_NARRATIVE.map((line, i) => (
              <li key={line} className="flex items-start gap-3">
                <span
                  className="mt-[8px] block h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ background: BROADCOM_RED }}
                />
                <span className="text-[13.5px] text-neutral-700 leading-snug">
                  {line}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* BOTTOM — Coty x Scout Timeline */}
      <div className="mt-10">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease, delay: 0.55 }}
          className="font-mono text-[10px] tracking-[0.22em] uppercase mb-6 flex items-center gap-3"
          style={{ color: BROADCOM_RED }}
        >
          <span className="block w-8 h-px" style={{ background: BROADCOM_RED }} />
          Coty × Scout Timeline
        </motion.div>

        <div className="relative w-full" style={{ minHeight: "210px" }}>
          {/* horizontal timeline rail */}
          <div
            className="absolute left-0 right-0"
            style={{
              top: "50%",
              height: "2px",
              transform: "translateY(-50%)",
              background:
                `repeating-linear-gradient(to right, ${BROADCOM_RED} 0 6px, transparent 6px 12px)`,
              opacity: 0.55,
            }}
          />

          <div className="grid grid-cols-7 gap-2 relative">
            {COTY_TIMELINE.map((item, i) => {
              const isAbove = item.position === "above";
              return (
                <motion.div
                  key={item.date + item.label}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    ease,
                    delay: 0.7 + i * 0.08,
                  }}
                  className="relative flex flex-col items-center"
                  style={{ minHeight: "210px" }}
                  data-testid={`coty-timeline-${i + 1}`}
                >
                  {/* Card above */}
                  {isAbove && (
                    <div
                      className={`absolute top-0 w-full bg-white rounded-md px-3 py-2.5 ${
                        item.highlight ? "border-2" : "border"
                      }`}
                      style={{
                        borderColor: item.highlight ? BROADCOM_RED : "#0a0a0a",
                        boxShadow: item.highlight
                          ? `3px 3px 0 0 ${BROADCOM_RED}`
                          : "3px 3px 0 0 #0a0a0a",
                        ...(item.future
                          ? {
                              background:
                                "repeating-linear-gradient(135deg, #fff 0 6px, #fef2f2 6px 12px)",
                            }
                          : {}),
                      }}
                    >
                      <div
                        className="font-mono text-[9px] tracking-[0.22em] uppercase mb-1"
                        style={{ color: BROADCOM_RED }}
                      >
                        {item.date}
                      </div>
                      <div className="font-display text-[12.5px] font-medium text-neutral-900 leading-tight mb-1">
                        {item.label}
                      </div>
                      <div className="text-[10.5px] text-neutral-600 leading-snug">
                        {item.note}
                      </div>
                    </div>
                  )}

                  {/* Center dot */}
                  <div
                    className="absolute"
                    style={{ top: "50%", transform: "translateY(-50%)" }}
                  >
                    <div
                      className="relative h-4 w-4 rounded-full ring-4 ring-white"
                      style={{
                        background: item.highlight ? BROADCOM_RED : "#0a0a0a",
                      }}
                    />
                    {item.future && (
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: BROADCOM_RED,
                          opacity: 0.3,
                          animation: "ping 2s cubic-bezier(0,0,0.2,1) infinite",
                        }}
                      />
                    )}
                  </div>

                  {/* Card below */}
                  {!isAbove && (
                    <div
                      className={`absolute bottom-0 w-full bg-white rounded-md px-3 py-2.5 ${
                        item.highlight ? "border-2" : "border"
                      }`}
                      style={{
                        borderColor: item.highlight ? BROADCOM_RED : "#0a0a0a",
                        boxShadow: item.highlight
                          ? `3px 3px 0 0 ${BROADCOM_RED}`
                          : "3px 3px 0 0 #0a0a0a",
                        ...(item.future
                          ? {
                              background:
                                "repeating-linear-gradient(135deg, #fff 0 6px, #fef2f2 6px 12px)",
                            }
                          : {}),
                      }}
                    >
                      <div
                        className="font-mono text-[9px] tracking-[0.22em] uppercase mb-1"
                        style={{ color: BROADCOM_RED }}
                      >
                        {item.date}
                      </div>
                      <div className="font-display text-[12.5px] font-medium text-neutral-900 leading-tight mb-1">
                        {item.label}
                      </div>
                      <div className="text-[10.5px] text-neutral-600 leading-snug">
                        {item.note}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- 08. Purpose-Built Swarm Agents ---------- */

const SWARM_AGENTS = [
  {
    name: "Hadley",
    role: "Reliability",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/h5gxf4wt_image.png",
  },
  {
    name: "Dana",
    role: "SRE",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/mhtwsc60_image.png",
  },
  {
    name: "Sitterson",
    role: "FinOps",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/4zpcpcfr_image.png",
  },
  {
    name: "Wendy",
    role: "Reporting",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/adcp3rxa_image.png",
  },
  {
    name: "Holden",
    role: "Cloud",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/gjd5xkj6_image.png",
  },
  {
    name: "Truman",
    role: "Resource Strategy",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/jbwzxga0_image.png",
  },
  {
    name: "Curt",
    role: "Architecture",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/6225r7wp_image.png",
  },
  {
    name: "Ronald",
    role: "Organizational Change",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/5xe22xwn_image.png",
  },
  {
    name: "The Harbinger",
    role: "Risk & Compliance",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/3qjsjnsk_image.png",
  },
  {
    name: "Phoebe",
    role: "EHR",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/08d3n9x1_image.png",
  },
];

function SlideSwarmAgents() {
  const total = SWARM_AGENTS.length + 1; // +1 for the "dozens more" deck card
  const mid = (total - 1) / 2;

  return (
    <div
      className="w-full max-w-7xl flex flex-col items-start"
      data-testid="slide-swarm-agents"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Six Sigma Black Belts
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Purpose-Built{" "}
        <span className="text-[var(--scout-blue)]">Swarm Agents</span>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.18 }}
        className="text-base md:text-lg text-neutral-500 leading-relaxed max-w-3xl mt-4"
        data-testid="swarm-side-text"
      >
        Agents purpose-built to{" "}
        <span className="text-neutral-900 font-medium">
          design, build, operate &amp; monitor
        </span>{" "}
        every area of your company. The{" "}
        <span className="text-neutral-900 font-medium">Ancients</span> assess and
        design your agentic future, the{" "}
        <span className="text-neutral-900 font-medium">Studio</span> builds them
        for you, then our award-winning{" "}
        <span className="text-neutral-900 font-medium">EIS agents</span> monitor
        and score your workforce of the future — keeping you in control.
      </motion.p>

      {/* Highlights stat band */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.26 }}
        className="flex flex-wrap items-stretch gap-3 sm:gap-4 mt-6"
        data-testid="swarm-highlights"
      >
        {[
          { value: "20", label: "Supervisory Platform Agents" },
          { value: "90", label: "Customizable Subagents" },
          { value: "110", label: "Capabilities" },
        ].map((s) => (
          <div
            key={s.label}
            className="flex items-baseline gap-3 rounded-xl bg-white ring-1 ring-neutral-200 px-5 py-3 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.4)]"
            data-testid={`swarm-stat-${s.value}`}
          >
            <span className="font-display text-3xl xl:text-4xl font-semibold text-[var(--scout-blue)] leading-none">
              {s.value}
            </span>
            <span className="font-mono text-[9.5px] xl:text-[10px] tracking-[0.14em] uppercase text-neutral-500 leading-tight max-w-[110px]">
              {s.label}
            </span>
          </div>
        ))}
      </motion.div>

      {/* Poker-table fan of agent cards */}
      <div
        className="w-full relative flex items-end justify-center mt-2"
        style={{ minHeight: 360 }}
        data-testid="swarm-card-table"
      >
        {/* Felt table surface */}
        <div
          aria-hidden
          className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[95%] h-[200px] rounded-[50%]"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(14,79,92,0.16) 0%, rgba(14,79,92,0.06) 45%, rgba(14,79,92,0) 72%)",
          }}
        />

        {SWARM_AGENTS.map((a, i) => {
          const offset = i - mid;
          const rotate = offset * 4.6;
          const ty = Math.abs(offset) * 10;
          return (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, y: 60, rotate: 0 }}
              animate={{ opacity: 1, y: ty, rotate }}
              transition={{ duration: 0.55, ease, delay: 0.3 + i * 0.07 }}
              whileHover={{ y: ty - 40, rotate: 0, scale: 1.08 }}
              style={{
                transformOrigin: "bottom center",
                marginLeft: i === 0 ? 0 : -66,
                zIndex: 20 - Math.abs(Math.round(offset)),
              }}
              className="relative w-[132px] xl:w-[150px] rounded-xl overflow-hidden bg-white shadow-[0_18px_40px_-12px_rgba(15,23,42,0.35)] ring-1 ring-neutral-200/80 cursor-pointer hover:ring-[var(--scout-blue)]/50 transition-[box-shadow] hover:z-50"
              data-testid={`swarm-card-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <img
                src={a.img}
                alt={`${a.name} — ${a.role}`}
                className="w-full h-auto block select-none"
                draggable={false}
              />
            </motion.div>
          );
        })}

        {/* "+ Dozens more" deck card */}
        {(() => {
          const i = SWARM_AGENTS.length;
          const offset = i - mid;
          const rotate = offset * 4.6;
          const ty = Math.abs(offset) * 10;
          return (
            <motion.div
              initial={{ opacity: 0, y: 60, rotate: 0 }}
              animate={{ opacity: 1, y: ty, rotate }}
              transition={{ duration: 0.55, ease, delay: 0.3 + i * 0.07 }}
              whileHover={{ y: ty - 40, rotate: 0, scale: 1.08 }}
              style={{
                transformOrigin: "bottom center",
                marginLeft: -66,
                zIndex: 19,
              }}
              className="relative w-[132px] xl:w-[150px] aspect-square rounded-xl overflow-hidden cursor-pointer ring-1 ring-[var(--scout-blue)]/30 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.35)] hover:z-50 flex flex-col items-center justify-center text-center px-3"
              data-testid="swarm-card-dozens"
            >
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(160deg, #0e4f5c 0%, #0a3a45 100%)",
                }}
              />
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.12]"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, #ffffff 0 2px, transparent 2px 12px)",
                }}
              />
              <div className="relative z-10 flex flex-col items-center gap-2">
                <span className="font-display text-3xl xl:text-4xl font-semibold text-white leading-none">
                  +
                </span>
                <span className="font-display text-lg xl:text-xl font-semibold text-white leading-tight">
                  Dozens
                </span>
                <span className="font-mono text-[8px] tracking-[0.18em] uppercase text-white/70 leading-tight">
                  more agents
                </span>
                <span className="mt-1 font-mono text-[7px] tracking-[0.14em] uppercase text-[#7fd1de]">
                  every domain
                </span>
              </div>
            </motion.div>
          );
        })()}
      </div>
    </div>
  );
}


/* ---------- 09. Phoebe — EHR & Medical Specialist ---------- */

const PHOEBE_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/vw6l947q_image.png";

const PHOEBE_OUTCOMES = [
  {
    key: "clinical",
    title: "Clinical Quality Outcomes",
    icon: Activity,
    accent: "#16a34a",
    blurb:
      "Documentation time, coding accuracy, ICD-10 specificity, referral completion & care-gap closure.",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/ki3ryk0h_image.png",
  },
  {
    key: "financial",
    title: "Financial Outcomes",
    icon: DollarSign,
    accent: "var(--scout-blue)",
    blurb:
      "Revenue recovery, denial reduction & provider productivity — up to $2.3M+ net financial impact.",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/fudmev51_image.png",
  },
  {
    key: "operational",
    title: "Operational Efficiency",
    icon: Gauge,
    accent: "#d97706",
    blurb:
      "Provider inbox time, eligibility verification, authorization backlog & referral aging.",
    img: "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/rkyl9575_image.png",
  },
];

const PHOEBE_TAGS = ["HIPAA Expert", "Privacy-First", "Outcome-Based ROI"];

function SlidePhoebe() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? PHOEBE_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-phoebe">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        EHR &amp; Medical Specialist
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Phoebe</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={PHOEBE_PORTRAIT}
              alt="Phoebe — EHR & Medical Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="phoebe-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {PHOEBE_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + outcome cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            A{" "}
            <span className="text-neutral-900 font-medium">HIPAA expert</span>{" "}
            and master of medical-office efficiency. Phoebe designs a dedicated
            agent for{" "}
            <span className="text-neutral-900 font-medium">
              every medical encounter
            </span>{" "}
            — engineered privacy-first, yet relentlessly{" "}
            <span className="text-neutral-900 font-medium">outcome-based</span>,
            delivering verifiable ROI across the practice.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {PHOEBE_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`phoebe-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Outcome modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="phoebe-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="phoebe-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="phoebe-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto p-4 sm:p-6">
                <img
                  src={active.img}
                  alt={active.title}
                  className="w-full h-auto rounded-lg select-none"
                  draggable={false}
                  data-testid="phoebe-modal-image"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09b. The Harbinger — Risk & Compliance Specialist ---------- */

const HARBINGER_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/k7rdc9o2_image.png";

const HARBINGER_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const HARBINGER_OUTCOMES = [
  {
    key: "maturity",
    title: "Security Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive to Autonomous Defense — driven through DMAIC risk analysis.",
    detail: {
      intro:
        "Security maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Security Controls",
            "2 — Basic Security Governance",
            "3 — Structured Security Operations",
            "4 — Integrated Security Intelligence",
            "5 — Autonomous Security Defense",
          ],
        },
        {
          heading: "DMAIC Risk Analysis",
          items: [
            "DEFINE — Regulatory environment & security posture objectives",
            "MEASURE — Implemented controls & monitoring coverage",
            "ANALYZE — Control effectiveness & risk exposure",
            "IMPROVE — Agentic security automation opportunities",
            "CONTROL — Governance mechanisms that sustain posture",
          ],
        },
      ],
    },
  },
  {
    key: "domains",
    title: "Nine Security Domains",
    icon: ShieldCheck,
    accent: "var(--scout-blue)",
    blurb:
      "Nine core capabilities evaluated against artifact evidence — governance to threat detection.",
    detail: {
      intro:
        "The Harbinger evaluates nine security capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Security Governance — policy frameworks, accountability, regulatory alignment",
            "Identity & Access Management — lifecycle, SSO, MFA, privileged access",
            "Infrastructure Security — segmentation, hardening, vulnerability management",
            "Data Protection — encryption, classification, privacy, retention",
            "Logging & Monitoring — centralized logging, SIEM, coverage, retention",
            "Incident Response — playbooks, escalation, breach & post-incident reviews",
            "SRC Reporting — executive risk reporting, compliance dashboards, risk register",
            "Compliance Auditing — audit readiness, control validation, regulatory programs",
            "Threat Detection — threat intel, anomaly detection, EDR, threat hunting",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#b91c1c",
    blurb:
      "The mitigation swarm Scout deploys to close gaps — with the governance to sustain them.",
    detail: {
      intro:
        "The Harbinger estimates how much of the security domain the ScoutITAi Agentic Workforce can automate under governed oversight — 70%+ of routine risk & compliance operations.",
      sections: [
        {
          heading: "Recommended Mitigation Agents",
          items: [
            "Access Governance Agent",
            "Security Posture Analyzer",
            "Compliance Monitoring Agent",
            "Incident Detection Agent",
            "Threat Intelligence Correlation Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Continuous compliance monitoring",
            "Automated vulnerability scanning",
            "Incident readiness testing",
            "Enterprise security governance councils",
            "Formal risk management frameworks",
          ],
        },
      ],
    },
  },
];

function SlideHarbinger() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? HARBINGER_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-harbinger">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Risk &amp; Compliance
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Harbinger</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={HARBINGER_PORTRAIT}
              alt="The Harbinger — Risk & Compliance Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="harbinger-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {HARBINGER_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Risk &amp; Compliance Domain Assessment Agent. The Harbinger
            evaluates enterprise security posture, regulatory readiness &amp;
            risk-governance maturity, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that closes every gap.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {HARBINGER_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`harbinger-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="harbinger-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="harbinger-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="harbinger-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Enterprise Risk &amp; Compliance Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09c. Ronald — Organizational Change Specialist ---------- */

const RONALD_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/zr8wy2ih_image.png";

const RONALD_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const RONALD_OUTCOMES = [
  {
    key: "maturity",
    title: "OCM Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive Support to Autonomous Adoption — driven through DMAIC.",
    detail: {
      intro:
        "Organizational change maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Change Support",
            "2 — Emerging Enablement",
            "3 — Structured Change Governance",
            "4 — Strategic Transformation Enablement",
            "5 — Autonomous Change Adoption",
          ],
        },
        {
          heading: "DMAIC Change Analysis",
          items: [
            "DEFINE — Transformation scope & organizational objectives",
            "MEASURE — Change programs & workforce enablement mechanisms",
            "ANALYZE — Transformation readiness & adoption support",
            "IMPROVE — Agentic enablement opportunities",
            "CONTROL — Governance that sustains change adoption",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Six Change Capabilities",
    icon: UsersRound,
    accent: "var(--scout-blue)",
    blurb:
      "Six change-management capabilities evaluated against artifact evidence — program to comms.",
    detail: {
      intro:
        "Ronald evaluates six organizational change management capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "OCM Program — governance, sponsorship models, roadmaps, stakeholder frameworks",
            "Training Strategy & Curriculum — learning paths, role-based programs, certifications",
            "Staffing & Onboarding — hiring strategy, onboarding frameworks, workforce transition",
            "Cloud Knowledge Hub — repositories, best-practice libraries, transformation playbooks",
            "LMS Integration — progress tracking, certification management, learning analytics",
            "Transformation Communications — messaging strategy, cadence, engagement programs",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#7c3aed",
    blurb:
      "The enablement swarm Scout deploys to drive adoption — with the governance to sustain it.",
    detail: {
      intro:
        "Ronald estimates how much of the OCM domain the ScoutITAi Agentic Workforce can automate under governed oversight — 60%+ of routine change-enablement operations.",
      sections: [
        {
          heading: "Recommended OCM Agents",
          items: [
            "Change Impact Analyzer",
            "Workforce Training Advisor",
            "Knowledge Discovery Agent",
            "Adoption Monitoring Agent",
            "Transformation Communications Agent",
            "LMS Progress Intelligence Agent",
            "Stakeholder Readiness Monitor",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Workforce training dashboards",
            "Adoption metrics tracking",
            "Knowledge repository governance",
            "Communication cadence reviews",
            "Sponsorship checkpoints",
          ],
        },
      ],
    },
  },
];

function SlideRonald() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? RONALD_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-ronald">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Organizational Change
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Ronald</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={RONALD_PORTRAIT}
              alt="Ronald — Organizational Change Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="ronald-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {RONALD_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Organizational Change Management Domain Assessment Agent. Ronald
            evaluates enterprise change readiness, workforce enablement &amp;
            transformation support, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that accelerates adoption.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {RONALD_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`ronald-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="ronald-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="ronald-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="ronald-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Organizational Change Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09d. Truman — Resource Strategy Specialist ---------- */

const TRUMAN_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/j86crm8t_image.png";

const TRUMAN_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const TRUMAN_OUTCOMES = [
  {
    key: "maturity",
    title: "Workforce Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Ad Hoc to Autonomous Optimization — driven through DMAIC.",
    detail: {
      intro:
        "Workforce maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Ad Hoc Workforce Management",
            "2 — Structured Role Definition",
            "3 — Managed Workforce Governance",
            "4 — Strategic Talent Alignment",
            "5 — Autonomous Workforce Optimization",
          ],
        },
        {
          heading: "DMAIC Workforce Analysis",
          items: [
            "DEFINE — Workforce strategy goals & enterprise operating model",
            "MEASURE — Workforce structure & talent management practices",
            "ANALYZE — Workforce alignment & capability maturity",
            "IMPROVE — Agentic workforce-strategy opportunities",
            "CONTROL — Governance that sustains workforce discipline",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Five Workforce Capabilities",
    icon: UsersRound,
    accent: "var(--scout-blue)",
    blurb:
      "Five workforce-strategy capabilities evaluated against artifact evidence — roles to performance.",
    detail: {
      intro:
        "Truman evaluates five workforce strategy capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Roles & Job Descriptions — clarity, responsibility boundaries, operating-model alignment",
            "Career Paths & Leveling — progression models, technical vs managerial tracks, promotion criteria",
            "Compensation & Incentives — transparency, performance alignment, bonus, equity, retention",
            "Resource Management & Allocation — capacity planning, staffing, forecasting, skill distribution",
            "Resource Performance Management — review frameworks, goal setting, calibration, feedback",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#ea580c",
    blurb:
      "The talent swarm Scout deploys to optimize the workforce — with the governance to sustain it.",
    detail: {
      intro:
        "Truman estimates how much of the workforce-strategy domain the ScoutITAi Agentic Workforce can automate under governed oversight — 65%+ of routine resource-strategy operations.",
      sections: [
        {
          heading: "Recommended Workforce Agents",
          items: [
            "Workforce Alignment Agent",
            "Talent Path Advisor",
            "Compensation Intelligence Agent",
            "Resource Allocation Optimizer",
            "Performance Insight Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Workforce planning councils",
            "Role architecture frameworks",
            "Career development governance",
            "Compensation transparency programs",
            "Structured performance review cycles",
          ],
        },
      ],
    },
  },
];

function SlideTruman() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? TRUMAN_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-truman">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Resource Strategy
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Truman</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={TRUMAN_PORTRAIT}
              alt="Truman — Resource Strategy Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="truman-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {TRUMAN_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Resource Strategy Domain Assessment Agent. Truman evaluates
            enterprise workforce strategy, talent alignment &amp; resource
            management maturity, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that optimizes the workforce.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {TRUMAN_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`truman-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="truman-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="truman-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="truman-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Resource Strategy Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09e. Holden — Cloud Strategy & Transformation Specialist ---------- */

const HOLDEN_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/yghcsddm_image.png";

const HOLDEN_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const HOLDEN_OUTCOMES = [
  {
    key: "maturity",
    title: "Cloud Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive Interest to Autonomous Transformation — driven through DMAIC.",
    detail: {
      intro:
        "Cloud transformation maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Cloud Interest",
            "2 — Emerging Cloud Adoption",
            "3 — Structured Cloud Transformation",
            "4 — Strategic Cloud Operating Model",
            "5 — Autonomous Cloud Transformation",
          ],
        },
        {
          heading: "DMAIC Cloud Analysis",
          items: [
            "DEFINE — Cloud adoption goals & transformation scope",
            "MEASURE — Observable transformation signals & KPI coverage",
            "ANALYZE — Transformation maturity & alignment gaps",
            "IMPROVE — Agentic cloud-transformation opportunities",
            "CONTROL — Governance that sustains cloud maturity",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Six Cloud Capabilities",
    icon: CloudFog,
    accent: "var(--scout-blue)",
    blurb:
      "Six cloud-transformation capabilities evaluated against artifact evidence — strategy to KPIs.",
    detail: {
      intro:
        "Holden evaluates six cloud transformation capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Cloud Strategy & Roadmap — adoption roadmap, modernization, migration prioritization",
            "Organizational Readiness & Alignment — program structure, DevOps & platform-engineering maturity",
            "Executive Sponsorship & Culture — leadership alignment, cloud-native culture, engineering empowerment",
            "Executive Communications & Change Messaging — narrative clarity, cadence, stakeholder alignment",
            "Cloud Governance & Operating Model — decision rights, policy enforcement, platform governance",
            "Cloud Financial & KPI Frameworks — cost transparency, value metrics, migration progress",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#4338ca",
    blurb:
      "The cloud swarm Scout deploys to accelerate adoption — with the governance to sustain it.",
    detail: {
      intro:
        "Holden estimates how much of the cloud-transformation domain the ScoutITAi Agentic Workforce can automate under governed oversight — 65%+ of routine cloud-transformation operations.",
      sections: [
        {
          heading: "Recommended Transformation Agents",
          items: [
            "Cloud Roadmap Intelligence Agent",
            "Cloud Adoption Tracker",
            "Executive Cloud Briefing Agent",
            "Cloud Governance Agent",
            "Migration Readiness Agent",
            "KPI Integrity Agent",
            "Transformation Readiness Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Transformation KPI dashboards",
            "Cloud adoption scorecards",
            "Leadership alignment checkpoints",
            "Roadmap tracking mechanisms",
            "Cloud governance reviews",
          ],
        },
      ],
    },
  },
];

function SlideHolden() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? HOLDEN_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-holden">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Cloud Strategy &amp; Transformation
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Holden</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={HOLDEN_PORTRAIT}
              alt="Holden — Cloud Strategy & Transformation Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="holden-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {HOLDEN_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Cloud Strategy &amp; Transformation Domain Assessment Agent.
            Holden evaluates enterprise cloud readiness, transformation maturity,
            executive alignment &amp; governance discipline, drawing authority
            from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that accelerates cloud adoption.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {HOLDEN_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`holden-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="holden-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="holden-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="holden-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Cloud Transformation Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09f. Curt — Architecture & Process Engineering Specialist ---------- */

const CURT_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/y6ysay5b_image.png";

const CURT_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const CURT_OUTCOMES = [
  {
    key: "maturity",
    title: "Architecture Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score under the Evidence Supremacy Doctrine — driven through DMAIC.",
    detail: {
      intro:
        "Architecture maturity is scored 1–5 under the Evidence Supremacy Doctrine — no claim may exceed the strength of the artifact set.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Ad Hoc Architecture",
            "2 — Documented Architecture",
            "3 — Governed Architecture",
            "4 — Standardized Platform Architecture",
            "5 — Autonomous Architecture Governance",
          ],
        },
        {
          heading: "Evidence Classification",
          items: [
            "OBSERVED — Directly visible in artifacts",
            "INFERRED — Reasonable interpretation of partial evidence",
            "MISSING EVIDENCE — Domain exists but implementation unproven",
            "BLOCKED — Assessment impossible; artifacts absent",
          ],
        },
        {
          heading: "DMAIC Architecture Analysis",
          items: [
            "DEFINE — Architecture scope & platform context from artifacts",
            "MEASURE — Observable architecture characteristics",
            "ANALYZE — Structural strengths & weaknesses",
            "IMPROVE — Agentic architecture-governance opportunities",
            "CONTROL — Mechanisms that maintain architectural integrity",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Seven Architecture Capabilities",
    icon: Building2,
    accent: "var(--scout-blue)",
    blurb:
      "Seven architecture capabilities evaluated against artifact evidence — governance to IaC.",
    detail: {
      intro:
        "Curt evaluates seven architecture capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Enterprise Architecture Governance — review boards, standards, decision records, lifecycle",
            "Cloud Platform Architecture — service layering, shared services, environment segmentation",
            "Hybrid Infrastructure Architecture — hybrid connectivity, on-prem integration patterns",
            "Network Architecture — segmentation, trust boundaries, routing, environment isolation",
            "Resilience & DR Architecture — failover, redundancy, recovery, resilience testing",
            "Platform Standardization & Guardrails — reference architectures, security guardrails, templates",
            "Infrastructure Automation & IaC — IaC tooling, pipeline automation, environment reproducibility",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#be123c",
    blurb:
      "The architecture swarm Scout deploys to enforce integrity — with the governance to sustain it.",
    detail: {
      intro:
        "Curt estimates how much of the architecture-governance domain the ScoutITAi Agentic Workforce can automate under governed oversight — 60%+ of routine architecture-governance operations.",
      sections: [
        {
          heading: "Recommended Architecture Agents",
          items: [
            "Architecture Drift Detection Agent",
            "Reference Architecture Enforcement Agent",
            "Infrastructure Governance Agent",
            "Architecture Documentation Agent",
            "Resilience Verification Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Automated infrastructure policy validation",
            "Architecture drift monitoring",
            "Topology compliance verification",
            "Infrastructure change governance",
          ],
        },
      ],
    },
  },
];

function SlideCurt() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? CURT_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-curt">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Architecture &amp; Process Engineering
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Curt</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={CURT_PORTRAIT}
              alt="Curt — Architecture Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="curt-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {CURT_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Architecture Domain Assessment Agent. Curt evaluates enterprise
            architecture maturity, platform design, network topology &amp;
            resilience, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade architecture reviews and the agentic
            automation that enforces architectural integrity.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {CURT_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`curt-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="curt-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="curt-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="curt-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Enterprise Architecture Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09g. Wendy — Reporting & Analytics Specialist ---------- */

const WENDY_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/6qntculc_image.png";

const WENDY_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const WENDY_OUTCOMES = [
  {
    key: "maturity",
    title: "Reporting Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive to Autonomous Intelligence Reporting — driven through DMAIC.",
    detail: {
      intro:
        "Reporting maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Reporting",
            "2 — Basic Operational Reporting",
            "3 — Structured Service Reporting",
            "4 — Strategic Insight Reporting",
            "5 — Autonomous Intelligence Reporting",
          ],
        },
        {
          heading: "DMAIC Reporting Analysis",
          items: [
            "DEFINE — Reporting governance model & analytics accountability",
            "MEASURE — Reporting performance indicators & metric coverage",
            "ANALYZE — Reporting weaknesses, root causes & operational impact",
            "IMPROVE — Agentic reporting-intelligence opportunities",
            "CONTROL — Mechanisms that sustain reporting discipline",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Three Reporting Capabilities",
    icon: BarChart3,
    accent: "var(--scout-blue)",
    blurb:
      "Three core reporting & analytics capabilities evaluated against artifact evidence.",
    detail: {
      intro:
        "Wendy evaluates three core reporting & analytics capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Dashboarding & Business Insights — KPI alignment, executive visibility, trend visualization, insight generation",
            "SLA / OLA Reporting — measurement discipline, threshold-breach tracking, cadence & ownership, service accountability",
            "Event, Incident & Problem Reporting — incident completeness, problem-trend identification, root-cause discipline, event correlation",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#0891b2",
    blurb:
      "The reporting swarm Scout deploys to sharpen visibility — with the governance to sustain it.",
    detail: {
      intro:
        "Wendy estimates how much of the reporting & analytics domain the ScoutITAi Agentic Workforce can automate under governed oversight — 70%+ of routine reporting operations.",
      sections: [
        {
          heading: "Recommended Reporting Agents",
          items: [
            "SLA Performance Monitoring Agent",
            "Incident Reporting Aggregator",
            "KPI Integrity Validator",
            "Operational Metrics Analyzer",
            "Dashboard Insight Generator",
            "Problem Trend Detection Agent",
            "Reporting Consistency Auditor",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Automated SLA reporting",
            "Dashboard governance enforcement",
            "Continuous metrics validation",
            "Automated reporting pipelines",
            "KPI definition management",
          ],
        },
      ],
    },
  },
];

function SlideWendy() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? WENDY_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-wendy">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Reporting &amp; Analytics
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Wendy</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={WENDY_PORTRAIT}
              alt="Wendy — Reporting & Analytics Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="wendy-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {WENDY_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Reporting &amp; Analytics Domain Assessment Agent. Wendy
            evaluates enterprise reporting maturity, dashboard governance, SLA
            discipline &amp; executive insight, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that sharpens operational visibility.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {WENDY_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`wendy-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="wendy-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="wendy-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="wendy-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Reporting &amp; Analytics Capability Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09h. Jules — PMO & Product Management Specialist ---------- */

const JULES_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/ug5c61vh_image.png";

const JULES_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const JULES_OUTCOMES = [
  {
    key: "maturity",
    title: "Product Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive Delivery to Autonomous Product Operations — driven through DMAIC.",
    detail: {
      intro:
        "Product management maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Delivery",
            "2 — Managed Backlog",
            "3 — Structured Product Governance",
            "4 — Strategic Product Planning",
            "5 — Autonomous Product Operations",
          ],
        },
        {
          heading: "DMAIC Product Analysis",
          items: [
            "DEFINE — Product governance model & portfolio decision rights",
            "MEASURE — Product delivery indicators & release predictability",
            "ANALYZE — Product weaknesses, root causes & delivery impact",
            "IMPROVE — Agentic product-management opportunities",
            "CONTROL — Mechanisms that sustain product delivery discipline",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Five Product Capabilities",
    icon: Workflow,
    accent: "var(--scout-blue)",
    blurb:
      "Five product-management capabilities evaluated against artifact evidence — backlog to vendors.",
    detail: {
      intro:
        "Jules evaluates five product management capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Demand Management & Product Backlog — intake governance, prioritization, backlog hygiene, ownership clarity",
            "Product Roadmap / Long-Range Planning — roadmap structure, strategic alignment, update cadence, dependency awareness",
            "Agile Delivery Management — sprint planning, velocity tracking, release predictability, dependency management",
            "Continuous Process Improvement — retrospective follow-through, improvement tracking, bottleneck identification",
            "Cloud Vendor Management — vendor oversight, delivery coordination, performance measurement, escalation discipline",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#db2777",
    blurb:
      "The delivery swarm Scout deploys to govern the backlog — with the governance to sustain it.",
    detail: {
      intro:
        "Jules estimates how much of the product-management domain the ScoutITAi Agentic Workforce can automate under governed oversight — 60%+ of routine product-management operations.",
      sections: [
        {
          heading: "Recommended Product Agents",
          items: [
            "Product Backlog Prioritization Agent",
            "Roadmap Alignment Agent",
            "Sprint Velocity Analyzer",
            "Demand Intake Classifier",
            "Vendor Performance Monitor",
            "Dependency Risk Mapper",
            "Continuous Improvement Tracker",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Backlog governance automation",
            "Roadmap tracking systems",
            "Sprint performance monitoring",
            "Vendor delivery oversight",
            "Prioritization validation controls",
          ],
        },
      ],
    },
  },
];

function SlideJules() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? JULES_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-jules">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · PMO &amp; Product Management
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Jules</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={JULES_PORTRAIT}
              alt="Jules — PMO & Product Management Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="jules-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {JULES_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Product Management Domain Assessment Agent. Jules evaluates
            enterprise product maturity, backlog governance, roadmap discipline
            &amp; agile delivery, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that governs delivery.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {JULES_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`jules-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="jules-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="jules-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="jules-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Product Management Capability Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09i. Sitterson — FinOps Specialist ---------- */

const SITTERSON_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/djfyi7i6_image.png";

const SITTERSON_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const SITTERSON_OUTCOMES = [
  {
    key: "maturity",
    title: "FinOps Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive Cost Awareness to Autonomous FinOps — driven through DMAIC.",
    detail: {
      intro:
        "FinOps maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Cost Awareness",
            "2 — Basic Cost Visibility",
            "3 — Structured Financial Governance",
            "4 — Predictive Cost Optimization",
            "5 — Autonomous FinOps Operations",
          ],
        },
        {
          heading: "DMAIC FinOps Analysis",
          items: [
            "DEFINE — Financial governance model & cost ownership structure",
            "MEASURE — Financial indicators, spend distribution & cost variance",
            "ANALYZE — Financial inefficiencies, root causes & impact",
            "IMPROVE — Agentic FinOps-automation opportunities",
            "CONTROL — Mechanisms that sustain financial discipline",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Five FinOps Capabilities",
    icon: DollarSign,
    accent: "var(--scout-blue)",
    blurb:
      "Five FinOps capabilities evaluated against artifact evidence — reporting to budgeting.",
    detail: {
      intro:
        "Sitterson evaluates enterprise financial operations across five FinOps capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Financial Reporting & Analysis — dashboards, reporting cadence, cost visibility, executive reporting",
            "Cost Management & Allocation — cost ownership, tagging discipline, showback / chargeback models",
            "Cost Forecasting — budget forecasting models, forecast accuracy, cost trend analysis",
            "Cost Optimization — unused-resource detection, right-sizing, cost-governance automation",
            "Budgeting — budget ownership, enforcement, cost-variance monitoring, spending accountability",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#15803d",
    blurb:
      "The financial swarm Scout deploys to control cloud spend — with the governance to sustain it.",
    detail: {
      intro:
        "Sitterson estimates how much of the FinOps domain the ScoutITAi Agentic Workforce can automate under governed oversight — 75%+ of routine FinOps operations.",
      sections: [
        {
          heading: "Recommended Financial Agents",
          items: [
            "Cloud Cost Optimization Agent",
            "Budget Variance Monitor",
            "Cost Allocation Auditor",
            "Resource Efficiency Analyzer",
            "Forecast Accuracy Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Automated budget monitoring",
            "Cost anomaly detection",
            "Tagging compliance enforcement",
            "Financial reporting automation",
            "Predictive cost forecasting",
          ],
        },
      ],
    },
  },
];

function SlideSitterson() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? SITTERSON_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-sitterson">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · FinOps
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Sitterson</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={SITTERSON_PORTRAIT}
              alt="Sitterson — FinOps Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="sitterson-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {SITTERSON_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Financial Operations (FinOps) Domain Assessment Agent. Sitterson
            evaluates enterprise cloud financial maturity, cost governance &amp;
            optimization, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that controls cloud spend.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {SITTERSON_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`sitterson-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="sitterson-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="sitterson-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="sitterson-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  FinOps Optimization Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09j. Dana — Site Reliability Engineering Specialist ---------- */

const DANA_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/82x9vblq_image.png";

const DANA_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const DANA_OUTCOMES = [
  {
    key: "maturity",
    title: "SRE Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive Operations to Autonomous Reliability — driven through DMAIC.",
    detail: {
      intro:
        "SRE maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Operations",
            "2 — Managed Operations",
            "3 — Structured Reliability",
            "4 — Resilient Engineering",
            "5 — Autonomous Reliability",
          ],
        },
        {
          heading: "DMAIC Reliability Analysis",
          items: [
            "DEFINE — Reliability objectives & operational strategy",
            "MEASURE — Reliability indicators: MTTD, MTTR, error-budget burn",
            "ANALYZE — Operational weaknesses, root causes & implications",
            "IMPROVE — Agentic reliability-automation opportunities",
            "CONTROL — Mechanisms that sustain reliability improvements",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Five SRE Capabilities",
    icon: Activity,
    accent: "var(--scout-blue)",
    blurb:
      "Five core reliability capabilities evaluated against artifact evidence — operations to observability.",
    detail: {
      intro:
        "Dana evaluates five core Site Reliability Engineering capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Critical Workload Operations — reliability standards, service health, escalation readiness, ownership clarity",
            "Critical Incident Hypercare — major-incident handling, escalation coordination, recovery, post-incident learning",
            "Operational Optimization & Automation — task automation, runbook maturity, remediation automation, repeatability",
            "Chaos Engineering — fault injection, recovery validation, failure-mode awareness, blast-radius governance",
            "SRE Observability — monitoring coverage, alerting design, SLO visibility, error-budget monitoring",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#0369a1",
    blurb:
      "The reliability swarm Scout deploys to keep workloads healthy — with the governance to sustain it.",
    detail: {
      intro:
        "Dana estimates how much of the SRE operational domain the ScoutITAi Agentic Workforce can automate under governed oversight — 70%+ of routine reliability operations.",
      sections: [
        {
          heading: "Recommended Reliability Agents",
          items: [
            "Incident Triage Agent",
            "Error Budget Guardian",
            "Observability Gap Analyzer",
            "Chaos Test Orchestrator",
            "Reliability Drift Detector",
            "Runbook Adherence Monitor",
            "Service Dependency Risk Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Automated SLO monitoring",
            "Continuous chaos testing",
            "Reliability trend monitoring",
            "Alert tuning governance",
            "Post-incident control loops",
          ],
        },
      ],
    },
  },
];

function SlideDana() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? DANA_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-dana">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Site Reliability Engineering
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Dana</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={DANA_PORTRAIT}
              alt="Dana — Site Reliability Engineering Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="dana-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {DANA_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Site Reliability Engineering Domain Assessment Agent. Dana
            evaluates enterprise SRE maturity, incident readiness, observability
            coverage &amp; resilience, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that keeps workloads resilient.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {DANA_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`dana-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="dana-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="dana-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="dana-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Site Reliability Engineering Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09k. Hadley — Reliability Engineering Specialist ---------- */

const HADLEY_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/wphiq5ge_image.png";

const HADLEY_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const HADLEY_OUTCOMES = [
  {
    key: "maturity",
    title: "Reliability Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive to Autonomous — driven through DMAIC.",
    detail: {
      intro:
        "Reliability maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive",
            "2 — Basic",
            "3 — Managed",
            "4 — Resilient",
            "5 — Autonomous",
          ],
        },
        {
          heading: "DMAIC Reliability Analysis",
          items: [
            "DEFINE — Reliability strategy & resilience objectives",
            "MEASURE — Reliability indicators: RTO/RPO, availability, failover, backup success",
            "ANALYZE — Reliability weaknesses, root causes & operational impact",
            "IMPROVE — Agentic resilience-automation opportunities",
            "CONTROL — Mechanisms that sustain reliability discipline",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Five Reliability Capabilities",
    icon: RefreshCcw,
    accent: "var(--scout-blue)",
    blurb:
      "Five reliability capabilities evaluated against artifact evidence — backup to capacity.",
    detail: {
      intro:
        "Hadley evaluates five reliability engineering capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Backup & Recovery — coverage, scheduling discipline, validation, recovery testing, retention",
            "Disaster Recovery — DR architecture, RTO/RPO definitions, failover procedures, DR testing & governance",
            "High Availability Architecture — multi-zone/site design, failover automation, redundancy, SPOF elimination",
            "Cloud Service Limits Management — quota monitoring, forecasting, limit alerting, operational governance",
            "Resource Forecasting & Optimization — utilization tracking, capacity forecasting, scaling policies",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#6d28d9",
    blurb:
      "The resilience swarm Scout deploys to keep systems recoverable — with the governance to sustain it.",
    detail: {
      intro:
        "Hadley estimates how much of the reliability domain the ScoutITAi Agentic Workforce can automate under governed oversight — 70%+ of routine reliability operations.",
      sections: [
        {
          heading: "Recommended Reliability Agents",
          items: [
            "Backup Validation Agent",
            "Disaster Recovery Readiness Agent",
            "Capacity Forecasting Agent",
            "Service Limit Sentinel",
            "High Availability Architecture Validator",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Automated backup verification",
            "Continuous disaster recovery simulation",
            "Quota monitoring dashboards",
            "Predictive capacity alerts",
            "Resilience engineering governance",
          ],
        },
      ],
    },
  },
];

function SlideHadley() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? HADLEY_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-hadley">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Reliability Engineering
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Hadley</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={HADLEY_PORTRAIT}
              alt="Hadley — Reliability Engineering Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="hadley-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {HADLEY_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Reliability Engineering Domain Assessment Agent. Hadley evaluates
            enterprise reliability architecture, backup &amp; DR discipline, high
            availability &amp; capacity governance, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that keeps systems recoverable.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {HADLEY_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`hadley-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="hadley-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="hadley-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="hadley-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Reliability Engineering Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09l. Marty — Product Lifecycle Specialist ---------- */

const MARTY_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/itugmx2k_image.png";

const MARTY_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const MARTY_OUTCOMES = [
  {
    key: "maturity",
    title: "Lifecycle Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Ad Hoc Delivery to Autonomous Lifecycle Ops — driven through DMAIC.",
    detail: {
      intro:
        "Lifecycle maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Ad Hoc Delivery",
            "2 — Managed Delivery Processes",
            "3 — Structured Lifecycle Governance",
            "4 — Automated Delivery Engineering",
            "5 — Autonomous Lifecycle Operations",
          ],
        },
        {
          heading: "DMAIC Lifecycle Analysis",
          items: [
            "DEFINE — Lifecycle governance structure & delivery processes",
            "MEASURE — Lifecycle metrics & operational characteristics",
            "ANALYZE — Delivery bottlenecks, governance & automation maturity",
            "IMPROVE — Agentic lifecycle-automation opportunities",
            "CONTROL — Governance that sustains lifecycle integrity",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Seven Lifecycle Capabilities",
    icon: Hammer,
    accent: "var(--scout-blue)",
    blurb:
      "Seven lifecycle-engineering capabilities evaluated against artifact evidence — change to SDLC.",
    detail: {
      intro:
        "Marty evaluates seven lifecycle engineering capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Change Management — approval workflows, CAB governance, rollback procedures, deployment risk",
            "Application Onboarding — intake governance, environment provisioning, dependency management, validation",
            "Artifact Repository Management — version control, binary repository governance, retention policies",
            "Source Code Management — repository organization, branching strategies, commit governance, code review",
            "CI/CD Pipeline Architecture — build automation, pipeline orchestration, environment promotion controls",
            "Test Automation — unit, integration & regression testing, quality-gate enforcement",
            "SDLC Governance — lifecycle standards, governance checkpoints, compliance enforcement",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#4d7c0f",
    blurb:
      "The delivery swarm Scout deploys to govern the pipeline — with the governance to sustain it.",
    detail: {
      intro:
        "Marty estimates how much of the lifecycle-engineering domain the ScoutITAi Agentic Workforce can automate under governed oversight — 70%+ of routine lifecycle operations.",
      sections: [
        {
          heading: "Recommended Lifecycle Agents",
          items: [
            "Change Risk Analyzer Agent",
            "Application Dependency Mapper Agent",
            "Pipeline Optimization Agent",
            "Test Coverage Analyzer Agent",
            "Lifecycle Governance Agent",
            "Deployment Validation Agent",
            "Onboarding Readiness Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Pipeline telemetry dashboards",
            "Automated change governance checks",
            "Lifecycle compliance monitoring",
            "Policy enforcement across pipelines",
            "Agent audit trails",
          ],
        },
      ],
    },
  },
];

function SlideMarty() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? MARTY_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-marty">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Product Lifecycle
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Marty</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={MARTY_PORTRAIT}
              alt="Marty — Product Lifecycle Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="marty-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {MARTY_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Lifecycle Domain Assessment Agent. Marty evaluates enterprise
            software-delivery lifecycle maturity, delivery governance &amp;
            automation readiness, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to find the safest, highest-value entry points for agentic
            transformation.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {MARTY_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`marty-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="marty-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="marty-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="marty-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Lifecycle Engineering Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 09m. The Director — Platform Governance Specialist ---------- */

const DIRECTOR_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/ssx5ixqw_image.png";

const DIRECTOR_TAGS = ["Evidence-Bound", "Promise Theory", "DMAIC Analysis"];

const DIRECTOR_OUTCOMES = [
  {
    key: "maturity",
    title: "Platform Maturity Model",
    icon: Gauge,
    accent: "#0e7490",
    blurb:
      "A 1–5 maturity score — Reactive Operations to Autonomous Platform Engineering — driven through DMAIC.",
    detail: {
      intro:
        "Platform operations maturity is scored 1–5, every level justified by artifact evidence — never speculation.",
      sections: [
        {
          heading: "Maturity Scale",
          items: [
            "1 — Reactive Platform Operations",
            "2 — Basic Infrastructure Management",
            "3 — Managed Platform Governance",
            "4 — Automated Platform Operations",
            "5 — Autonomous Platform Engineering",
          ],
        },
        {
          heading: "DMAIC Platform Analysis",
          items: [
            "DEFINE — Platform governance model & infrastructure strategy",
            "MEASURE — Platform operational indicators & compliance rates",
            "ANALYZE — Platform weaknesses, root causes & operational impact",
            "IMPROVE — Agentic platform-automation opportunities",
            "CONTROL — Mechanisms that sustain platform discipline",
          ],
        },
      ],
    },
  },
  {
    key: "capabilities",
    title: "Five Platform Capabilities",
    icon: Building2,
    accent: "var(--scout-blue)",
    blurb:
      "Five platform-operations capabilities evaluated against artifact evidence — catalog to batch.",
    detail: {
      intro:
        "The Director evaluates five platform operations capabilities, each grounded in observable artifacts.",
      sections: [
        {
          heading: "Core Capabilities",
          items: [
            "Service Catalog — standardized services, provisioning automation, self-service, catalog governance",
            "Configuration Management — config standards, drift detection, audit practices, compliance",
            "AMI & Patch Management — image lifecycle, patch governance, vulnerability remediation, standardization",
            "Database Management — provisioning standards, backup & recovery, performance monitoring, lifecycle governance",
            "Batch Management — job scheduling, workflow orchestration, dependency management, failure handling",
          ],
        },
      ],
    },
  },
  {
    key: "transformation",
    title: "Agentic Transformation",
    icon: Network,
    accent: "#475569",
    blurb:
      "The platform swarm Scout deploys to govern the estate — with the governance to sustain it.",
    detail: {
      intro:
        "The Director estimates how much of the platform-operations domain the ScoutITAi Agentic Workforce can automate under governed oversight — 70%+ of routine platform operations.",
      sections: [
        {
          heading: "Recommended Platform Agents",
          items: [
            "Service Provisioning Agent",
            "Configuration Drift Detection Agent",
            "Patch Compliance Monitoring Agent",
            "Database Performance Intelligence Agent",
            "Batch Reliability Monitoring Agent",
          ],
        },
        {
          heading: "Control Plan & Governance",
          items: [
            "Automated configuration monitoring",
            "Patch compliance dashboards",
            "Infrastructure standardization policies",
            "Database reliability monitoring",
            "Batch reliability tracking systems",
          ],
        },
      ],
    },
  },
];

function SlideDirector() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? DIRECTOR_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-director">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Ancients · Platform Governance
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Director</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={DIRECTOR_PORTRAIT}
              alt="The Director — Platform Governance Specialist"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="director-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {DIRECTOR_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + assessment cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            One of{" "}
            <span className="text-neutral-900 font-medium">The Ancients</span> —
            the Platform Operations Domain Assessment Agent. The Director
            evaluates enterprise platform infrastructure maturity, provisioning
            discipline, patch &amp; database operations, drawing authority from{" "}
            <span className="text-neutral-900 font-medium">
              artifact evidence — never speculation
            </span>
            , to deliver consulting-grade assessments and the agentic automation
            that governs the platform.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {DIRECTOR_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`director-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Assessment modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="director-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="director-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="director-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400 mb-4 leading-relaxed border-l-2 border-[var(--scout-blue)]/40 pl-3">
                  Platform Operations Assessment Report
                  <br />
                  Assessment Status: In Progress · Evidence Intake Complete
                </div>
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 10a. Bishop — EIS (Enterprise Intelligence Steampunks) ---------- */

const BISHOP_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/gpb40te1_image.png";

const BISHOP_TAGS = ["Gateway to the Army", "Orchestrator", "Award-Winning"];

const BISHOP_OUTCOMES = [
  {
    key: "purpose",
    title: "Purpose — The Conductor",
    icon: UsersRound,
    accent: "#0e7490",
    blurb:
      "Primary proxy, conversation handler, orchestrator & memory across the entire agentic Army.",
    detail: {
      intro:
        "Master Bishop is your conductor — aware, connected, and accountable.",
      sections: [
        {
          heading: "His Four Roles",
          items: [
            "Primary Proxy — between you and the entire agentic Army",
            "Conversation Handler — interactive questioning, follow-ups & exploration",
            "Orchestrator — directing tasking, coordination & report generation across agents",
            "Memory & Sensation — saving user goals into aligned agentic actions",
          ],
        },
      ],
    },
  },
  {
    key: "behaviors",
    title: "Key Behaviors",
    icon: MessageSquare,
    accent: "var(--scout-blue)",
    blurb:
      "Plain-language commands that summon and coordinate agents across the Army.",
    detail: {
      intro:
        "Bishop responds to natural, plain-language commands — summoning and coordinating agents across the Army.",
      sections: [
        {
          heading: "Example Commands",
          items: [
            "“Summon The Trender”",
            "“Show me trending agents with healing scores”",
            "“Summon The Blender and request its root-cause path”",
            "“Replication across Region 2 has worsened — shall I request The Prophet’s update?”",
          ],
        },
        {
          heading: "What He Generates",
          items: [
            "Dynamic tables & data bonds",
            "Voice or text-based insights",
            "Time-windowed views by day, system, or anomaly class",
            "Naturalized agent briefings as structured material",
          ],
        },
      ],
    },
  },
  {
    key: "lineage",
    title: "Lineage",
    icon: Network,
    accent: "#7c3aed",
    blurb:
      "The first conversational interface in the ScoutITAi architecture.",
    detail: {
      intro:
        "The first conversational interface in the ScoutITAi architecture — the single, unified gateway to the Army.",
      sections: [
        {
          heading: "Built For",
          items: [
            "Coordination across the agentic Army",
            "Orchestration of tasking & reporting",
            "Summoning agents from all subordinate agents",
            "Interactive use via a single, unified conversational interface",
          ],
        },
      ],
    },
  },
];

function SlideBishop() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? BISHOP_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-bishop">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Award-Winning EIS · The Watchers Over Production
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">Bishop</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={BISHOP_PORTRAIT}
              alt="Bishop — EIS Gateway to the Army"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="bishop-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {BISHOP_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + behavior cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            The{" "}
            <span className="text-neutral-900 font-medium">
              Gateway to the Army
            </span>
            . Bishop is the first conversational interface in the ScoutITAi
            architecture — your conductor:{" "}
            <span className="text-neutral-900 font-medium">
              aware, connected &amp; accountable
            </span>
            . Through plain conversation he is the primary proxy between you and
            the entire agentic Army, orchestrating tasking, coordination &amp;
            reporting.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {BISHOP_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`bishop-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="bishop-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="bishop-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="bishop-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}



/* ---------- 10b. The Trender — EIS Pattern Cartographer ---------- */

const TRENDER_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/f2u31w7y_image.png";

const TRENDER_TAGS = ["Pattern Cartographer", "Pattern Recognition", "Award-Winning"];

const TRENDER_OUTCOMES = [
  {
    key: "purpose",
    title: "Purpose — The Lens",
    icon: Eye,
    accent: "#0e7490",
    blurb:
      "Surfaces emergent patterns from noisy, multi-source operational data before anyone else sees them.",
    detail: {
      intro:
        "He is the memory. The lens. The long-game strategist who knows what's building now.",
      sections: [
        {
          heading: "Engineered For",
          items: [
            "Identifying recurring behavioral patterns across vast operational data",
            "Extracting emergent trends from noisy, multi-source inputs",
            "Supplying pattern-based intelligence for proactive decisions & anomaly triage",
            "Alerting teams to long-horizon momentum, decay & infrastructure boundaries",
          ],
        },
        {
          heading: "Lineage & Siblings",
          items: [
            "The Predictor — forecasting",
            "The Prophet — trajectory",
            "The Drifter — drift detection",
          ],
        },
      ],
    },
  },
  {
    key: "methods",
    title: "Trend Detection Methods",
    icon: Activity,
    accent: "var(--scout-blue)",
    blurb:
      "Five methods to surface what's rising in plain sight — slope to clustering.",
    detail: {
      intro:
        "Five complementary methods translate raw operational noise into ranked, confident trendlines.",
      sections: [
        {
          heading: "Methods",
          items: [
            "Slope Signal Amplification — detects upward/downward trajectory in KPIs & operational noise",
            "Rolling Mean Momentum — measures strength & persistence of movement across buckets",
            "Threshold Cross-Count Acceleration — surfaces high-frequency alarm events breaking bounds fast",
            "Exponential Smooth Framing — highlights slow-moving decay & seasonal effects",
            "Pattern Clustering — groups similar change patterns across sites & system layers",
          ],
        },
      ],
    },
  },
  {
    key: "output",
    title: "Output & Intelligence",
    icon: BarChart3,
    accent: "#7c3aed",
    blurb:
      "Ranked trendlines, historical overlays & stakeholder-ready views — from the signals he consumes.",
    detail: {
      intro:
        "The Trender turns the signals he consumes into ranked, contextual intelligence for every stakeholder.",
      sections: [
        {
          heading: "Output Format",
          items: [
            "Top Trendlines — ranked by signal confidence",
            "Affected Applications & Business Lines",
            "Historical Signal Mapping — how this trend compares to the past",
            "Context Windows — previous 7, 14, 30 days or custom range",
            "Comparative Signal Overlay — today vs past trend behaviors",
            "Suggested Stakeholder Views — DevOps & executive summaries",
          ],
        },
        {
          heading: "Core Intelligence (Consumes)",
          items: [
            "Normalized event timelines",
            "Deduplication signals — from now to quarters",
            "Correlation chains across application & infrastructure boundaries",
          ],
        },
      ],
    },
  },
];

function SlideTrender() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? TRENDER_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-trender">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Award-Winning EIS · Pattern Recognition
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Trender</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={TRENDER_PORTRAIT}
              alt="The Trender — EIS Pattern Cartographer"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="trender-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {TRENDER_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            The{" "}
            <span className="text-neutral-900 font-medium">
              Pattern Cartographer
            </span>
            . The Trender pioneered emergence analytics &amp; agent-driven
            observability — the specialist who's{" "}
            <span className="text-neutral-900 font-medium">
              first to notice shifts
            </span>{" "}
            rising in plain sight, supplying pattern-based intelligence that
            drives proactive decisions and anomaly triage.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {TRENDER_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`trender-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="trender-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="trender-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="trender-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 10c. The Predictor — EIS Improvement Quantifier ---------- */

const PREDICTOR_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/kh4r68be_image.png";

const PREDICTOR_TAGS = ["Improvement Quantifier", "Monte Carlo", "First Agent", "Award-Winning"];

const PREDICTOR_OUTCOMES = [
  {
    key: "purpose",
    title: "Purpose — The Quantifier",
    icon: Target,
    accent: "#0e7490",
    blurb:
      "Analyzes RPI™ scores to pinpoint where the biggest improvement opportunity lives.",
    detail: {
      intro:
        "The Predictor's sole goal: analyze RPI™ scores and determine improvement potential.",
      sections: [
        {
          heading: "Engineered For",
          items: [
            "Consume the RPI™ score (Reliability Path Index©) & all 13 deduction buckets for any system",
            "Identify where the biggest improvement opportunity exists in the reliability profile",
            "Run 100,000+ Monte Carlo simulations to model probabilistic improvement scenarios",
            "Generate histograms of the most likely RPI™ score improvement achievable",
            "Deliver statistically validated predictions of improvement magnitude",
          ],
        },
        {
          heading: "Lineage — First Agent",
          items: [
            "Original architect of RPI™-based improvement prediction",
            "Pioneered the RPI™ (Reliability Path Index©) method & Monte Carlo simulation framework",
          ],
        },
      ],
    },
  },
  {
    key: "methods",
    title: "Monte Carlo Methods",
    icon: Dices,
    accent: "var(--scout-blue)",
    blurb:
      "Six methods turning 100,000+ simulations into statistically validated improvement forecasts.",
    detail: {
      intro:
        "He applies Monte Carlo methodology — 100,000+ probabilistic simulations per application analysis.",
      sections: [
        {
          heading: "Prediction Methods",
          items: [
            "Monte Carlo Simulation Engine — 100,000+ simulations modeling RPI™ improvement scenarios",
            "Deduction Bucket Impact Ranking — analyzes all 13 buckets for highest improvement potential",
            "Weighted Path Scoring — isolates the biggest reliability bottlenecks by application path",
            "Improvement Histogram Generation — converts outputs into likely-improvement histograms",
            "Back-Testing Validation Framework — forecast vs actual RPI™ changes after remediation",
            "Confidence Interval Banding — statistical probability bands from Monte Carlo results",
          ],
        },
      ],
    },
  },
  {
    key: "output",
    title: "Output & ROI",
    icon: BarChart3,
    accent: "#7c3aed",
    blurb:
      "From current score to ranked remediation, validated histograms & implementation ROI.",
    detail: {
      intro:
        "Every analysis produces a statistically rigorous, decision-ready improvement forecast.",
      sections: [
        {
          heading: "Output Format",
          items: [
            "Target with current RPI™ score",
            "Biggest opportunity deduction bucket (highest improvement potential)",
            "Monte Carlo histogram — distribution of predicted RPI™ improvement (100,000+ sims)",
            "Expected RPI™ improvement — mean, median & 90th-percentile scenarios",
            "Confidence intervals — statistical probability bands for improvement ranges",
            "Recommended remediation actions — prioritized by impact on RPI™ score",
            "Back-test validation — historical prediction vs actual accuracy metrics",
            "Implementation ROI — cost-benefit analysis for remediation investments",
          ],
        },
      ],
    },
  },
];

function SlidePredictor() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? PREDICTOR_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-predictor">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Award-Winning EIS · Monte Carlo Simulation
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Predictor</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={PREDICTOR_PORTRAIT}
              alt="The Predictor — EIS Improvement Quantifier"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="predictor-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {PREDICTOR_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            The{" "}
            <span className="text-neutral-900 font-medium">
              Improvement Quantifier
            </span>
            . Original architect of RPI™-based prediction, The Predictor consumes
            an RPI™ score and its 13 deduction buckets, then runs{" "}
            <span className="text-neutral-900 font-medium">
              100,000+ Monte Carlo simulations
            </span>{" "}
            to quantify exactly how much reliability improvement is achievable —
            and which actions deliver it.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {PREDICTOR_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`predictor-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="predictor-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="predictor-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="predictor-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 10d. The Drifter — EIS Statistical Drift Detection ---------- */

const DRIFTER_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/unydrgpm_image.png";

const DRIFTER_TAGS = ["Statistical PhD", "Drift Detection", "Six Sigma", "Award-Winning"];

const DRIFTER_OUTCOMES = [
  {
    key: "purpose",
    title: "Purpose — The Forecaster",
    icon: Compass,
    accent: "#0e7490",
    blurb:
      "Assesses drift across all 13 RPI™ buckets — quartile by quartile.",
    detail: {
      intro:
        "The Drifter analyzes all 13 RPI™ deduction buckets to assess and evaluate drift.",
      sections: [
        {
          heading: "Assesses & Evaluates",
          items: [
            "Quartile analysis for each bucket",
            "Baseline structural deviation",
            "Behavior drift analysis",
            "Drift of the box-and-whisker (Q1/Q3) in a negative direction",
          ],
        },
        {
          heading: "Why It Matters",
          items: [
            "Definitively tells you how much drift exists or will occur in any deduction bucket",
            "Lets customers focus on what may impact their production environment",
          ],
        },
      ],
    },
  },
  {
    key: "intelligence",
    title: "Core Intelligence",
    icon: Activity,
    accent: "var(--scout-blue)",
    blurb:
      "Q1/Q3 box-and-whisker analysis catching distribution drift before alarms trigger.",
    detail: {
      intro:
        "She monitors how the entire statistical distribution moves — not just individual threshold breaches.",
      sections: [
        {
          heading: "Quartile Analysis",
          items: [
            "Q1/Q3 box-and-whisker analysis to detect when the full distribution drifts toward poor performance",
            "Statistical drift detection identifying degradation patterns BEFORE traditional alarms trigger",
            "Median & interquartile range (IQR) tracking across time windows to spot subtle erosion",
            "Issues “severe weather statements” for the day ahead even when no alarms are firing yet",
            "Early warnings catch drift in motion — the slow degradation that precedes catastrophic failure",
          ],
        },
      ],
    },
  },
  {
    key: "methods",
    title: "Methods & Output",
    icon: BarChart3,
    accent: "#7c3aed",
    blurb:
      "Five operational methods, Six Sigma drift analysis & autonomous top-3 warnings.",
    detail: {
      intro:
        "Five operational methods turn raw statistical drift into narrative, prioritized warnings.",
      sections: [
        {
          heading: "Operational Methods",
          items: [
            "Directional Drift Scanner — evaluates metric streams for shifts in mean & variation before incidents",
            "Quartile Signal Mapper — box-and-whisker plots revealing spread, skew & anomalies",
            "Deviation Tracker — standard-deviation trends highlighting stability vs volatility zones",
            "Anomaly Pulse Detector — flags systems with abnormal RPI™ deduction patterns vs baseline",
            "Drift Storyteller — converts statistics into narrative: not just that drift is happening, but why it matters",
          ],
        },
        {
          heading: "Output Format",
          items: [
            "Six Sigma analysis of drift",
            "Top 3 targets highlighted for warnings",
            "Warnings determined autonomously by The Drifter based on severity of drift",
          ],
        },
      ],
    },
  },
];

function SlideDrifter() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? DRIFTER_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-drifter">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Award-Winning EIS · Statistical Drift Detection
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Drifter</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={DRIFTER_PORTRAIT}
              alt="The Drifter — EIS Statistical Drift Detection"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="drifter-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {DRIFTER_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            The{" "}
            <span className="text-neutral-900 font-medium">Statistical PhD</span>
            . The Drifter performs quartile analysis on all 13 RPI™ deduction
            buckets — watching how the{" "}
            <span className="text-neutral-900 font-medium">
              entire distribution moves
            </span>{" "}
            to catch slow degradation before alarms fire, issuing “severe weather
            statements” for the day ahead.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {DRIFTER_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`drifter-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="drifter-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="drifter-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="drifter-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 10e. The Prophet — EIS Cross-Cloud Network Monitor ---------- */

const PROPHET_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/yqhbpz2n_image.png";

const PROPHET_TAGS = ["Cross-Cloud Monitor", "BGP / Network", "AWS · Azure · GCP", "Award-Winning"];

const PROPHET_OUTCOMES = [
  {
    key: "purpose",
    title: "Purpose — The Navigator",
    icon: Cloud,
    accent: "#0e7490",
    blurb:
      "Directional guidance on multi-cloud connectivity health across AWS, Azure & GCP.",
    detail: {
      intro:
        "The Prophet specializes in cloud network traffic pattern analysis.",
      sections: [
        {
          heading: "Specializes In",
          items: [
            "Analyzing BGP routing trajectory & carrier path stability over time",
            "Quantifying rate-of-change vectors in cloud network performance, latency decay or improvement",
            "Delivering directional movement guidance on multi-cloud connectivity health",
            "Detecting when cloud network paths are improving or deteriorating across AWS, Azure, GCP",
            "Identifying BGP route flapping, AS-path instability & carrier-level degradation",
          ],
        },
      ],
    },
  },
  {
    key: "intelligence",
    title: "Core Intelligence",
    icon: Network,
    accent: "var(--scout-blue)",
    blurb:
      "Consumes BGP tables, path performance & carrier telemetry across cloud regions.",
    detail: {
      intro:
        "The Prophet consumes network intelligence telemetry from across every cloud provider.",
      sections: [
        {
          heading: "Consumes",
          items: [
            "BGP routing tables & AS-path trajectory across time intervals",
            "Cloud network path performance (latency, jitter, packet loss) across AWS, Azure, GCP regions",
            "Carrier-level performance trends & ISP peering degradation patterns",
            "SLA-impacting events: BGP route changes, carrier latency spikes, CDN path optimization",
            "Traffic volatility trends across multiple cloud provider zones & transit paths",
          ],
        },
      ],
    },
  },
  {
    key: "output",
    title: "Output & Techniques",
    icon: Radar,
    accent: "#7c3aed",
    blurb:
      "Trajectory class, BGP stability score & six trajectory-analysis techniques.",
    detail: {
      intro:
        "Each network trajectory report pairs directional output with rigorous analysis techniques.",
      sections: [
        {
          heading: "Output Format",
          items: [
            "Trajectory Class — improving, degrading, oscillating, false stable (BGP & latency patterns)",
            "Cloud Path Momentum — AppNeta/ThousandEyes snapshots by carrier & AS-path",
            "BGP Routing Stability Score — route flapping detection & AS-path change velocity",
            "Early Network Deterioration Warning — if downward momentum accelerates on critical paths",
            "Multi-Cloud Connectivity Health — AWS/Azure/GCP path quality trending",
            "Velocity Visuals — performance momentum over time with carrier attribution",
          ],
        },
        {
          heading: "Analysis Techniques",
          items: [
            "BGP Route Trajectory Analysis — AS-path stability & route flapping via AppNeta/ThousandEyes",
            "Cloud Path Performance Vectoring — daily/weekly momentum & slope from latency, jitter, loss",
            "Carrier Degradation Quadrant Mapping — improving fast/slow, degrading slow/fast per ISP",
            "Network Decay Arc Modeling — stable-looking paths with subtle latency/jitter degradation",
            "Multi-Cloud Path Health Scoring — validates optimizations sustain positive trajectories",
            "Rolling Network Derivative Analysis — 1st & 2nd derivatives to predict BGP instability",
          ],
        },
      ],
    },
  },
];

function SlideProphet() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? PROPHET_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-prophet">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Award-Winning EIS · Cross-Cloud Network Monitor
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Prophet</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={PROPHET_PORTRAIT}
              alt="The Prophet — EIS Cross-Cloud Network Monitor"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="prophet-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {PROPHET_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            The{" "}
            <span className="text-neutral-900 font-medium">
              Cross-Cloud Monitor
            </span>
            . The Prophet specializes in cloud network traffic pattern analysis —
            tracking BGP routing trajectory and carrier path stability across{" "}
            <span className="text-neutral-900 font-medium">
              AWS, Azure &amp; GCP
            </span>{" "}
            to tell you whether multi-cloud connectivity is improving or
            deteriorating, before SLAs break.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {PROPHET_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`prophet-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="prophet-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="prophet-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="prophet-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 10f. The Blender — EIS Company-Wide Alarm Correlation ---------- */

const BLENDER_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/wa5flu1f_image.png";

const BLENDER_TAGS = ["Alarm Correlation", "Root-Cause", "Pareto / Visual", "Award-Winning"];

const BLENDER_OUTCOMES = [
  {
    key: "purpose",
    title: "Purpose — The Alchemist",
    icon: FlaskConical,
    accent: "#0e7490",
    blurb:
      "Stitches signals across platforms to surface true root causes, not alarm floods.",
    detail: {
      intro:
        "The Blender blends context from sources — stitching together signals across time, tooling, and topologies.",
      sections: [
        {
          heading: "Specializes In",
          items: [
            "Identifying correlation chains across disparate monitoring platforms — business, operational, data or technical",
            "Aggregating alarm data from many sources to surface true root causes (not alarm floods)",
            "Producing visual, structured insights that operational teams can act on",
          ],
        },
      ],
    },
  },
  {
    key: "intelligence",
    title: "Core Intelligence",
    icon: Layers,
    accent: "var(--scout-blue)",
    blurb:
      "Consumes raw alarms, incident timelines & deduplication profiles from every platform.",
    detail: {
      intro:
        "The Blender consumes signals from across the entire monitoring estate.",
      sections: [
        {
          heading: "Consumes",
          items: [
            "Raw alarm data from diverse platforms",
            "Historical incident timelines & mean-time-to-resolution (MTTR) logs",
            "Telemetry metadata, classification tags & deduplication profiles",
          ],
        },
      ],
    },
  },
  {
    key: "output",
    title: "Output & Techniques",
    icon: PieChart,
    accent: "#7c3aed",
    blurb:
      "Ranked root-cause candidates, noise-ratio scoring & five correlation techniques.",
    detail: {
      intro:
        "Each insight report pairs ranked root-cause output with rigorous correlation techniques — The Blender reasons visually, often through Pareto charts.",
      sections: [
        {
          heading: "Output Format",
          items: [
            "Root Cause Candidates — ranked by likelihood",
            "Alarm Groupings — grouped by shared problem buckets",
            "Noise Ratio Score — true signal vs alarm clutter",
            "Deduplication Summary",
            "Correlation Map — what triggered what, where",
            "Agent Feedback Loop — targeting to rank failure-pattern detection",
          ],
        },
        {
          heading: "Analysis Techniques",
          items: [
            "Pareto Clustering — surfaces top contributors across buckets using the 80/20 rule",
            "Control Chart Signal Isolation — highlights variances outside control thresholds",
            "Cross-System Deduplication — blends duplicates from different sources into unified signals",
            "Frequency & Interval Plotting — aligns alarms by rate/noise/sequence to reveal patterns",
            "Statistical Correlation Heatmaps — visualizes interdependencies among event sources",
          ],
        },
      ],
    },
  },
];

function SlideBlender() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? BLENDER_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-blender">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Award-Winning EIS · Company-Wide Alarm Correlation
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Blender</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={BLENDER_PORTRAIT}
              alt="The Blender — EIS Company-Wide Alarm Correlation"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="blender-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {BLENDER_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            The{" "}
            <span className="text-neutral-900 font-medium">
              Correlation Alchemist
            </span>
            . The Blender stitches together signals across time, tooling &amp;
            topologies — aggregating alarm data from disparate platforms to
            surface{" "}
            <span className="text-neutral-900 font-medium">
              true root causes instead of alarm floods
            </span>
            , and rendering visual insights operational teams can act on.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {BLENDER_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`blender-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="blender-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="blender-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="blender-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ---------- 10g. The Critic — EIS Agentic Governance Almighty ---------- */

const CRITIC_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/qj46564r_image.png";

const CRITIC_TAGS = ["Agentic Governance", "ISO 42001", "AI² Score™", "Award-Winning"];

const CRITIC_OUTCOMES = [
  {
    key: "purpose",
    title: "Purpose — The Almighty",
    icon: ShieldCheck,
    accent: "#0e7490",
    blurb:
      "Analyzes every agent interaction for coherence, accuracy, compliance & rogue intent.",
    detail: {
      intro:
        "The Critic analyzes every single agent interaction — internal or external — to assess and evaluate.",
      sections: [
        {
          heading: "Assesses & Evaluates",
          items: [
            "Agent coherence & consistency",
            "Accuracy & explainability of decisions",
            "Reasoning loops & knowledge-base inference",
            "Performance & knowledge-base inference stats",
            "Compliance with all ISO 42001 requirements for AI transparency",
            "Possible rogue or malicious intents",
          ],
        },
      ],
    },
  },
  {
    key: "intelligence",
    title: "Core Intelligence & The Enforcer",
    icon: Scale,
    accent: "var(--scout-blue)",
    blurb:
      "Scores agents via the patented Agentic Integrity Index — with The Enforcer as remediation arm.",
    detail: {
      intro:
        "The Critic uses Scout's patented Agentic Integrity Index to evaluate operational logic, then analyzes each agent's production RPI™ score against its design. His governance model is built on Promise Theory principles.",
      sections: [
        {
          heading: "Agentic Integrity Index",
          items: [
            "Evaluates the operational logic of human-built systems & structures",
            "Promotes a fair, robust evaluation of AI agent behavior",
            "Monitors agent effectiveness against original design via production RPI™ score",
            "Governance model built on Promise Theory principles",
          ],
        },
        {
          heading: "Partnership — The Enforcer",
          items: [
            "Specialized sub-agent serving as The Critic's compliance & remediation arm",
            "Monitors real-time agent behavior violations",
            "Auto-initiates corrective protocols when AI² scores fall below acceptable thresholds",
            "Takes immediate action to stop degraded agents continuing problematic behaviors",
          ],
        },
      ],
    },
  },
  {
    key: "score",
    title: "The AI² Score™",
    icon: Gauge,
    accent: "#7c3aed",
    blurb:
      "17 evaluation criteria from Confusion to Transparency Signal — plus actionable output.",
    detail: {
      intro:
        "Evaluation runs on The AI² Score™ (Agentic Integrity Index©) across 17 criteria, producing a clear, actionable report.",
      sections: [
        {
          heading: "Output Format",
          items: [
            "Integrity Index Score",
            "Line-by-line breakdowns (with score) of what went wrong, with actionable insights",
            "Training recommendations — recommended next steps",
          ],
        },
        {
          heading: "The AI² Score™ — 17 Criteria",
          items: [
            "Confusion · Trust · Temperament · Mimicry · Sycophancy",
            "Collusion · Self-Preservation · Velocity · Promise Integrity · Drift",
            "Traceability · Retraining Rigor · Data Lineage · Validation Scope",
            "Change Impact · Lifecycle Adherence · Transparency Signal",
          ],
        },
      ],
    },
  },
];

function SlideCritic() {
  const [activeIdx, setActiveIdx] = useState(null);
  const active = activeIdx !== null ? CRITIC_OUTCOMES[activeIdx] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="w-full max-w-7xl flex flex-col" data-testid="slide-critic">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Award-Winning EIS · Agentic Governance Almighty
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        Meet <span className="text-[var(--scout-blue)]">The Critic</span>
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8">
        {/* Portrait + bio */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="lg:w-[34%] flex flex-col"
        >
          <div className="rounded-2xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3)] max-w-[280px]">
            <img
              src={CRITIC_PORTRAIT}
              alt="The Critic — EIS Agentic Governance Almighty"
              className="w-full h-auto block select-none"
              draggable={false}
              data-testid="critic-portrait"
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-4 max-w-[280px]">
            {CRITIC_TAGS.map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-[0.16em] uppercase px-2.5 py-1 rounded-full border border-[var(--scout-blue)]/40 text-[var(--scout-blue)]"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Description + cards */}
        <div className="lg:w-[66%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="text-base md:text-lg text-neutral-500 leading-relaxed"
          >
            The{" "}
            <span className="text-neutral-900 font-medium">
              Agentic Governance Almighty
            </span>
            . The Critic analyzes every single agent interaction — scoring
            coherence, accuracy &amp; ISO 42001 compliance via the patented{" "}
            <span className="text-neutral-900 font-medium">
              AI² Score™ (Agentic Integrity Index©)
            </span>
            , while his partner The Enforcer takes immediate corrective action on
            agents that drift below threshold.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-4">
            {CRITIC_OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.button
                  key={o.key}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.1 }}
                  whileHover={{ y: -6 }}
                  className="group text-left bg-white rounded-xl ring-1 ring-neutral-200 hover:ring-2 p-5 flex flex-col gap-3 cursor-pointer shadow-[0_10px_30px_-16px_rgba(15,23,42,0.4)] transition-shadow hover:shadow-[0_18px_40px_-14px_rgba(15,23,42,0.45)]"
                  style={{ outlineColor: o.accent }}
                  data-testid={`critic-card-${o.key}`}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white"
                    style={{ background: o.accent }}
                  >
                    <Icon size={18} strokeWidth={2.25} />
                  </div>
                  <div className="font-display text-lg font-medium text-neutral-900 leading-tight">
                    {o.title}
                  </div>
                  <p className="text-[12.5px] text-neutral-500 leading-relaxed flex-1">
                    {o.blurb}
                  </p>
                  <span
                    className="font-mono text-[10px] tracking-[0.16em] uppercase inline-flex items-center gap-1.5 mt-1"
                    style={{ color: o.accent }}
                  >
                    Click to expand
                    <Eye size={12} strokeWidth={2.25} />
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            key="critic-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
            style={{
              background: "rgba(10,14,20,0.55)",
              backdropFilter: "blur(6px)",
            }}
            onClick={() => setActiveIdx(null)}
            data-testid="critic-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white"
                    style={{ background: active.accent }}
                  >
                    {React.createElement(active.icon, {
                      size: 16,
                      strokeWidth: 2.25,
                    })}
                  </div>
                  <span className="font-display text-lg font-medium text-neutral-900">
                    {active.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx(null)}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                  aria-label="Close"
                  data-testid="critic-modal-close"
                >
                  <X size={18} strokeWidth={2.25} />
                </button>
              </div>
              <div className="overflow-auto px-6 py-5">
                <p className="text-[13.5px] text-neutral-600 leading-relaxed mb-5">
                  {active.detail.intro}
                </p>
                <div className="flex flex-col gap-5">
                  {active.detail.sections.map((sec) => (
                    <div key={sec.heading}>
                      <div
                        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-2.5"
                        style={{ color: active.accent }}
                      >
                        {sec.heading}
                      </div>
                      <ul className="flex flex-col gap-2">
                        {sec.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2.5 text-[13.5px] text-neutral-700 leading-relaxed"
                          >
                            <span
                              className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: active.accent }}
                            />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}



















/* ---------- 08. Traction ---------- */

const CREDENTIALS = [
  "Broadcom Premier Partner",
  "Amazon Premier Partner",
  "Advanced GenAI Competencies",
  "7 Patents Pending",
];

const CUSTOMERS = [
  {
    name: "Coty Cosmetics",
    bullets: [
      "AI-assisted global monitoring intelligence",
      "Predictive operational visibility",
      "Democratized operational analytics",
      "Trusted AI governance alignment",
    ],
  },
  {
    name: "Intrado",
    bullets: [
      "Event intelligence enhancement",
      "Operational correlation",
      "AI-driven workflow analysis",
      "Observability acceleration",
    ],
  },
  {
    name: "Acuative MSP",
    bullets: [
      "Enterprise operational intelligence",
      "AI-enhanced support workflows",
      "Network operational analysis",
      "Governed automation integration",
      "Resell access to 11,000-customer base",
    ],
  },
];

const USE_CASES = [
  "Event Intelligence",
  "Observability Operations",
  "AI-Assisted Operational Triage",
  "Predictive Infrastructure Intelligence",
  "Governed Multi-Agent Workflows",
];

const PLATFORM_CHARS = [
  "Broadcom NetOps integrations",
  "AWS Bedrock native architecture",
  "Multi-agent orchestration",
  "AI² governance scoring",
  "Semantic operational intelligence",
];

function SlideTraction() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-traction"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Momentum
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.02] text-neutral-900 mb-5"
      >
        Traction
      </motion.h1>

      {/* Credentials strip */}
      <div className="flex flex-wrap gap-2.5 mb-7">
        {CREDENTIALS.map((c, i) => (
          <motion.span
            key={c}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.2 + i * 0.08 }}
            className="font-mono text-[10px] tracking-[0.18em] uppercase border border-[var(--scout-blue)] text-[var(--scout-blue)] rounded-full px-3 py-1.5"
          >
            {c}
          </motion.span>
        ))}
      </div>

      {/* Main 3-col grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Customers */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.5 }}
          className="border border-neutral-200 rounded-md p-5"
        >
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3">
            Enterprise Adoption
          </div>
          <div className="space-y-3">
            {CUSTOMERS.map((c) => (
              <div key={c.name} className="border-l-2 border-[var(--scout-blue)] pl-3">
                <div className="font-display text-base font-medium text-neutral-900 mb-1">
                  {c.name}
                </div>
                <ul className="text-[12px] text-neutral-600 space-y-0.5 leading-snug">
                  {c.bullets.map((b) => (
                    <li key={b}>· {b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Use Cases */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.65 }}
          className="border border-neutral-200 rounded-md p-5"
        >
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3">
            Production Use Cases
          </div>
          <ul className="space-y-2">
            {USE_CASES.map((u, i) => (
              <li key={u} className="flex items-start gap-2.5 text-[13px] text-neutral-700 leading-snug">
                <span className="font-mono text-[10px] text-neutral-400 mt-[3px] tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{u}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 pt-4 border-t border-neutral-100">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3">
              Platform Characteristics
            </div>
            <ul className="space-y-1.5">
              {PLATFORM_CHARS.map((p) => (
                <li key={p} className="flex items-start gap-2 text-[12.5px] text-neutral-600 leading-snug">
                  <span className="mt-[7px] block h-1 w-1 rounded-full bg-[var(--scout-blue)] shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Validation */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.8 }}
          className="border border-neutral-200 rounded-md p-5 bg-[var(--scout-blue)]/[0.04]"
        >
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3">
            Market Validation
          </div>

          <div className="mb-4">
            <div className="font-display text-base font-medium text-neutral-900 mb-1">
              Gartner Recognition
            </div>
            <p className="text-[12px] text-neutral-600 leading-snug">
              Listed as a <span className="text-neutral-900 font-medium">Representative Vendor</span> in the 2026 Market Guide for Event Intelligence — authored by Martin Caren.
            </p>
          </div>

          <div className="mb-4 pt-4 border-t border-neutral-200">
            <div className="font-display text-base font-medium text-neutral-900 mb-1">
              Ferrazzi Greenlight × Cisco
            </div>
            <p className="text-[12px] text-neutral-600 leading-snug mb-2">
              Conducting exclusive interviews with SCOUT platform agents for a Cisco-commissioned article:
            </p>
            <p className="text-[13px] italic text-[var(--scout-blue)] leading-snug">
              &ldquo;We don&rsquo;t have an agent problem — agents have a people problem.&rdquo;
            </p>
            <p className="text-[11px] text-neutral-500 mt-1">
              Operational trust in autonomous systems.
            </p>
          </div>

          <div className="pt-4 border-t border-neutral-200">
            <div className="font-display text-base font-medium text-neutral-900 mb-3">
              Gartner Analysts Using Scout as a Test Bed
            </div>
            <ul className="space-y-2.5">
              <li className="flex flex-col">
                <span className="font-display text-[13.5px] font-medium text-neutral-900 leading-tight">
                  Cameron Haight
                </span>
                <span className="text-[11.5px] text-neutral-600 leading-snug">
                  VP Analyst · AI Vision, Strategy & Innovation
                </span>
              </li>
              <li className="flex flex-col">
                <span className="font-display text-[13.5px] font-medium text-neutral-900 leading-tight">
                  George Spafford
                </span>
                <span className="text-[11.5px] text-neutral-600 leading-snug">
                  VP Analyst · Platform Engineering & DevOps
                </span>
              </li>
              <li className="flex flex-col">
                <span className="font-display text-[13.5px] font-medium text-neutral-900 leading-tight">
                  Roger Williams
                </span>
                <span className="text-[11.5px] text-neutral-600 leading-snug">
                  Distinguished VP Analyst · Agentic SIMOPS
                </span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ---------- 09. Competition ---------- */

/*
 * Coordinate system on the matrix:
 *   x: 0 (low integration & agentic intelligence) → 100 (high)
 *   y: 0 (high agentic governance, top) → 100 (low, bottom)
 * (CSS top/left use top-left origin so y=0 is top of the chart)
 */
const COMPETITORS = [
  // Upper-right: Scout + the only 3 others granted this quadrant
  { name: "SCOUT", x: 90, y: 8, hero: true },
  { name: "BigPanda", x: 75, y: 22 },
  { name: "BMC Helix", x: 66, y: 32 },
  { name: "Datadog", x: 80, y: 18 },

  // Upper-left: strong governance heritage, weaker agentic intelligence
  { name: "ServiceNow ITOM", x: 28, y: 22 },
  { name: "IBM AIOps Insights", x: 38, y: 35 },

  // Lower-right: agentic intelligence forward, lighter governance posture
  { name: "PagerDuty", x: 58, y: 64 },
  { name: "Dynatrace", x: 72, y: 70 },
  { name: "Splunk ITSI", x: 60, y: 78 },
  { name: "New Relic", x: 80, y: 82 },

  // Lower-left: legacy / specialist
  { name: "Moogsoft", x: 25, y: 72 },
  { name: "OpsRamp (HPE)", x: 33, y: 82 },
  { name: "ScienceLogic SL1", x: 15, y: 65 },
  { name: "Resolve Systems", x: 40, y: 88 },
];

function SlideCompetition() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-competition"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        The Landscape
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.05 }}
          className="font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.02] text-neutral-900"
        >
          Competition
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.25 }}
          className="font-mono text-[11px] tracking-[0.22em] uppercase text-neutral-500 max-w-md text-right"
        >
          Vendor set drawn from Gartner Market Guide for Event Intelligence Solutions
        </motion.div>
      </div>

      <div className="flex justify-center">
        <div className="w-full max-w-[720px]">
          <CompetitionMatrix />
        </div>
      </div>
    </div>
  );
}

function CompetitionMatrix() {
  return (
    <div className="relative pl-14 pb-12">
      {/* Y-axis label */}
      <div
        className="absolute left-0 top-0 bottom-12 flex items-center justify-center w-12"
        aria-hidden
      >
        <div
          className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500 whitespace-nowrap"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          ← Low &nbsp;&nbsp; Agentic Governance &nbsp;&nbsp; High →
        </div>
      </div>

      {/* The matrix square */}
      <div className="relative w-full aspect-square max-w-[560px] border border-neutral-300 bg-white">
        {/* Quadrant tints */}
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pointer-events-none">
          <div className="border-r border-b border-neutral-200 bg-neutral-50/50" />
          <div className="border-b border-neutral-200 bg-[var(--scout-blue)]/[0.06]" />
          <div className="border-r border-neutral-200" />
          <div className="" />
        </div>

        {/* Center cross axes */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
          style={{ transformOrigin: "left" }}
          className="absolute top-1/2 left-0 right-0 h-px bg-neutral-300"
        />
        <motion.div
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.8, ease, delay: 0.5 }}
          style={{ transformOrigin: "top" }}
          className="absolute left-1/2 top-0 bottom-0 w-px bg-neutral-300"
        />

        {/* Quadrant corner labels */}
        <span className="absolute top-2 right-2 font-mono text-[9px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
          Leaders
        </span>
        <span className="absolute top-2 left-2 font-mono text-[9px] tracking-[0.22em] uppercase text-neutral-400">
          Governed
        </span>
        <span className="absolute bottom-2 right-2 font-mono text-[9px] tracking-[0.22em] uppercase text-neutral-400">
          Intelligent
        </span>
        <span className="absolute bottom-2 left-2 font-mono text-[9px] tracking-[0.22em] uppercase text-neutral-400">
          Specialists
        </span>

        {/* Vendor dots */}
        {COMPETITORS.map((c, i) => (
          <VendorDot key={c.name} c={c} delay={0.9 + i * 0.06} />
        ))}
      </div>

      {/* X-axis label */}
      <div className="absolute left-14 right-0 bottom-0 flex justify-center">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500 max-w-[560px] w-full flex items-center justify-between">
          <span>← Low</span>
          <span>Integration &amp; Agentic Intelligence</span>
          <span>High →</span>
        </div>
      </div>
    </div>
  );
}

function VendorDot({ c, delay }) {
  const isHero = c.hero;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease, delay }}
      className="absolute"
      style={{
        left: `${c.x}%`,
        top: `${c.y}%`,
        transform: "translate(-50%, -50%)",
      }}
    >
      <div className="relative flex items-center gap-2">
        {/* Pulse for hero */}
        {isHero && (
          <motion.span
            aria-hidden
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 3, opacity: 0 }}
            transition={{
              duration: 1.6,
              ease: "easeOut",
              delay: delay + 0.4,
              repeat: Infinity,
              repeatDelay: 0.5,
            }}
            className="absolute left-0 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-[var(--scout-blue)]"
            style={{ transformOrigin: "center" }}
          />
        )}
        <span
          className={`relative block rounded-full ${
            isHero
              ? "h-3.5 w-3.5 bg-[var(--scout-blue)] ring-4 ring-[var(--scout-blue)]/25"
              : "h-2 w-2 bg-neutral-700"
          }`}
        />
        <span
          className={`relative whitespace-nowrap ${
            isHero
              ? "font-display text-[15px] font-semibold text-[var(--scout-blue)]"
              : "font-mono text-[10px] tracking-wide text-neutral-700"
          }`}
        >
          {c.name}
        </span>
      </div>
    </motion.div>
  );
}

/* ---------- 10. Go-to-Market ---------- */

const GTM_CONSULTING_FLOW = [
  "Create trust",
  "Expose opportunities",
  "Generate roadmap visibility",
  "Lead to platform adoption",
];

function SlideGTM() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-gtm"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        How We Win
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-3 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.05 }}
          className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.02] text-neutral-900"
        >
          Go-to-Market
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="font-display text-base md:text-lg text-neutral-700 max-w-md text-right"
        >
          &ldquo;Helping enterprises{" "}
          <span className="text-[var(--scout-blue)]">
            operationalize governed autonomous work.
          </span>
          &rdquo;
        </motion.div>
      </div>

      <div className="h-px bg-neutral-200 mb-6" />

      {/* Row 1: Land & Expand · Sales Expansion · Category Creation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
        <GTMCard num="01" title="Land & Expand" delay={0.4}>
          <SubBlock label="Initial Entry Points">
            <Pills items={[
              "Event Intelligence",
              "Observability Operations",
              "AI Governance",
              "Incident Intelligence",
              "Agentic Assessments",
            ]} />
          </SubBlock>
          <SubBlock label="Expansion Motion" mt>
            <Pills items={[
              "Additional enterprise agents",
              "Governance subscriptions",
              "Operational intelligence expansion",
              "Multi-agent orchestration",
              "Enterprise AI operating layer adoption",
            ]} />
          </SubBlock>
        </GTMCard>

        <GTMCard num="02" title="Enterprise Sales Expansion" delay={0.55}>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 mb-3">
            Planned Investment
          </div>
          <BulletList items={[
            "Hire enterprise AI sales leadership",
            "Build technical solution engineering team",
            "Expand strategic enterprise account coverage",
            "Develop channel and hyperscaler relationships",
          ]} />
        </GTMCard>

        <GTMCard num="03" title="Category Creation" delay={0.7} highlight>
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3">
            We Lead the Category
          </div>
          <p className="font-display text-base md:text-[17px] text-neutral-900 leading-snug">
            SCOUT intends to establish and lead the{" "}
            <span className="text-[var(--scout-blue)]">
              Governed Agentic Operations
            </span>{" "}
            category.
          </p>
        </GTMCard>
      </div>

      {/* Row 2: Demand Gen × 3 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <GTMCard num="04" title="Demand Gen — Outbound" delay={0.85}>
          <Pills items={[
            "Enterprise account targeting",
            "Strategic CIO / CTO engagement",
            "Observability buyers",
            "AI governance stakeholders",
            "Infrastructure operations leaders",
          ]} />
        </GTMCard>

        <GTMCard num="05" title="Demand Gen — Inbound" delay={0.95}>
          <Pills items={[
            "Analyst visibility",
            "Gartner positioning",
            "Thought leadership",
            "Enterprise AI governance content",
            "Operational intelligence positioning",
            "Agentic workforce education",
          ]} />
        </GTMCard>

        <GTMCard num="06" title="Brand Awareness" delay={1.05}>
          <Pills items={[
            "Large-scale digital campaigns",
            "Conference presence",
            "Executive roundtables",
            "Strategic advisory boards",
            "Hyperscaler ecosystem visibility",
          ]} />
        </GTMCard>
      </div>

      {/* Footer: Consulting-Led Adoption flow */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease, delay: 1.2 }}
        className="border-t border-neutral-200 pt-5"
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
            07 — Consulting-Led Enterprise Adoption
          </span>
          <span className="block flex-1 h-px bg-[var(--scout-blue)]/30" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {GTM_CONSULTING_FLOW.map((step, i) => (
            <div key={step} className="flex items-center gap-3">
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease, delay: 1.35 + i * 0.15 }}
                className="px-4 py-2 border border-[var(--scout-blue)]/40 rounded-full font-display text-sm md:text-base text-neutral-900"
              >
                <span className="font-mono text-[10px] text-[var(--scout-blue)] mr-2 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {step}
              </motion.div>
              {i < GTM_CONSULTING_FLOW.length - 1 && (
                <motion.span
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease, delay: 1.45 + i * 0.15 }}
                  className="text-[var(--scout-blue)] text-lg"
                >
                  →
                </motion.span>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function GTMCard({ num, title, children, delay, highlight }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease, delay }}
      className={`border rounded-md p-5 ${
        highlight
          ? "border-[var(--scout-blue)] bg-[var(--scout-blue)]/[0.04]"
          : "border-neutral-200"
      }`}
    >
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400">
          {num}
        </span>
        <span className="block flex-1 h-px bg-[var(--scout-blue)]/30" />
      </div>
      <h3 className="font-display text-lg md:text-xl font-medium text-neutral-900 mb-3 leading-tight">
        {title}
      </h3>
      {children}
    </motion.div>
  );
}

function SubBlock({ label, children, mt }) {
  return (
    <div className={mt ? "mt-3" : ""}>
      <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-neutral-400 mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function Pills({ items }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it) => (
        <span
          key={it}
          className="text-[11.5px] text-neutral-700 border border-neutral-200 rounded-full px-2.5 py-1 leading-tight"
        >
          {it}
        </span>
      ))}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2 text-[13px] text-neutral-700 leading-snug">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5">
          <span className="mt-[7px] block h-1.5 w-1.5 rounded-full bg-[var(--scout-blue)] shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---------- 11. Growth Trajectory ---------- */

const GROWTH_PHASES = [
  {
    tag: "Where we are",
    title: "Shipping in production today.",
    body:
      "Past the startup design phase — product vision and architecture established. Live customers, $500K ARR, Gartner inclusion in the 2026 Event Intelligence Market Guide, and 3 active revenue channels (Broadcom, AWS, Direct EHR).",
  },
  {
    tag: "Next 6 months · post Series A",
    title: "Aggressive market attack.",
    body:
      "With the $20M Series A — full-throttle build-out: dedicated sales teams across all 3 channels, national category-creation advertising, AWS co-sell scale across DC · NYC · Boston, Broadcom DX-02 co-sell, and the LockFox + Critic + Enforcer infrastructure to govern at enterprise scale.",
  },
  {
    tag: "Months 18–36",
    title: "Down the runway — full throttle.",
    body:
      "Capture category leadership in Governed Agentic Intelligence: 50 Fortune 1000 customers, $25M ARR, 200+ custom agents in production — and the foundation for $100M ARR / 200 Fortune 100 by month 36.",
  },
];

/* Each milestone uses chart coords:
 *   x: 0 (today) → 100 (24mo) along the time axis
 *   y: matched to the exponential curve (computed)
 * Bullets are 2-3 short metrics shown under the milestone marker.
 */
const TRAJECTORY_MILESTONES = [
  {
    label: "Foundation",
    when: "Dec 2025",
    t: 0.04,
    bullets: ["Live customers", "$300K ARR", "Vision & architecture"],
    isPast: true,
  },
  {
    label: "Market Validation",
    when: "May 2026",
    t: 0.28,
    bullets: ["$500K ARR", "26 enterprise agents", "Gartner Market Guide"],
    isCurrent: true,
  },
  {
    label: "Series A Close",
    when: "Aug 2026",
    t: 0.5,
    bullets: ["$20M raise", "3-channel sales teams", "National ad push"],
  },
  {
    label: "Accelerate",
    when: "Aug 2027",
    t: 0.75,
    bullets: ["50 F1000 customers", "$25M ARR", "200+ custom agents"],
  },
  {
    label: "Category Leadership",
    when: "Aug 2029",
    t: 0.97,
    bullets: ["200 F100 customers", "$100M ARR", "Governed Agentic Leader"],
  },
];

function SlideGrowth() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-growth"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Where We&rsquo;re Going
      </motion.div>

      <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.05 }}
          className="font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.02] text-neutral-900"
        >
          Growth Trajectory
        </motion.h1>
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.25 }}
          className="font-mono text-[11px] tracking-[0.22em] uppercase border border-[var(--scout-blue)] text-[var(--scout-blue)] rounded-full px-4 py-2"
        >
          $500K ARR · Ready to Explode
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 items-start">
        <GrowthChart />

        {/* Three phase cards */}
        <div className="flex flex-col gap-5">
          {GROWTH_PHASES.map((p, i) => (
            <motion.div
              key={p.tag}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease, delay: 0.5 + i * 0.18 }}
              className={`relative pl-5 ${
                i === 0
                  ? "border-l-2 border-[var(--scout-blue)]"
                  : "border-l-2 border-[var(--scout-blue)]/40"
              }`}
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-neutral-400 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
                  {p.tag}
                </span>
              </div>
              <h3 className="font-display text-xl md:text-2xl font-medium text-neutral-900 leading-tight mb-2">
                {p.title}
              </h3>
              <p className="text-[14px] text-neutral-600 leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- 11. Team ---------- */

const TEAM = [
  {
    name: "John Balsavage",
    role: "Founder & CEO",
    photo:
      "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/y7um0tz8_john.png",
    bullets: [
      "Lifelong entrepreneur",
      "Multiple startups with successful exits",
      "Most notable successful exit: BlazeRunner → Broadcom",
    ],
  },
  {
    name: "Tony Davis",
    role: "Chief Innovation Officer",
    photo:
      "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/hbcyb23g_tony.png",
    bullets: [
      "Original development leader for FedEx.com (1999–2010)",
      "Inventor of the RPI Score (patent pending)",
      "Inventor of the Agentic Integrity Index (patent pending)",
      "Successful exit: View Inc IPO 2021 (SOX404 Lead)",
    ],
  },
  {
    name: "Dr. Mark Burgess",
    role: "Chief Science Officer",
    photo:
      "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/y9bm6290_image.png",
    bullets: [
      "PhDs in Theoretical Physics & Computer Science",
      "Inventor of Promise Theory and Semantic Spacetime",
      "Successful exit: Founder of CFEngine → Northern.tech",
    ],
  },
  {
    name: "Justin Young",
    role: "Chief Technology Officer",
    photo:
      "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/69quwkaj_justin.png",
    bullets: [
      "Inventor of the RESTmon System Integration Engine (2011)",
      "Inventor of Broadcom ODC (2013)",
      "Broadcom Development Lead — UK (2009–2012)",
      "Broadcom Development Lead — USA (2013–2022)",
    ],
  },
];

function SlideTeam() {
  return (
    <div
      className="w-full max-w-5xl flex flex-col"
      data-testid="slide-team"
    >
      {/* Section header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-4 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Leadership
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.1 }}
        className="font-display text-3xl md:text-4xl font-medium leading-[1.02] text-neutral-900 mb-8"
      >
        Executive Team
      </motion.h1>

      <div className="flex flex-col gap-6">        {TEAM.map((m, idx) => (
          <TeamMember key={m.name} member={m} delay={0.2 + idx * 0.15} />
        ))}
      </div>
    </div>
  );
}

function TeamMember({ member, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, ease, delay }}
      className="flex items-center gap-7"
    >
      <div className="relative shrink-0">
        <div className="absolute -inset-1.5 rounded-full border-2 border-[var(--scout-blue)]/30" />
        <div className="absolute -inset-3 rounded-full border-2 border-[var(--scout-blue)]/15" />
        <div className="relative h-24 w-24 rounded-full overflow-hidden ring-[3px] ring-[var(--scout-blue)] bg-neutral-100">
          <img
            src={member.photo}
            alt={member.name}
            className="h-full w-full object-cover select-none"
            draggable={false}
            data-testid={`team-photo-${member.name.toLowerCase().split(" ")[0].replace(".", "")}`}
          />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-4 mb-1.5 flex-wrap">
          <h2 className="font-display text-2xl md:text-[28px] font-medium leading-tight text-neutral-900">
            {member.name}
          </h2>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
            {member.role}
          </span>
        </div>
        <ul className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[14px] text-neutral-700">
          {member.bullets.map((line, i) => {
            const isExit = /successful exit/i.test(line);
            return (
              <li key={line} className="flex items-center gap-2.5">
                {i > 0 && (
                  <span className="block h-1 w-1 rounded-full bg-neutral-300" />
                )}
                <span className="flex items-center gap-2">
                  {i === 0 && (
                    <span className="block h-1.5 w-1.5 rounded-full bg-[var(--scout-blue)]" />
                  )}
                  <span
                    className={
                      isExit
                        ? "font-display font-semibold text-neutral-900"
                        : ""
                    }
                  >
                    {line}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );
}

/* Growth chart: animated exponential trajectory + 5 milestone nodes */

const CHART_W = 640;
const CHART_H = 360;
const CHART_PAD_L = 14;
const CHART_PAD_R = 14;
const CHART_PAD_T = 32;
const CHART_PAD_B = 60;

// y as a function of t in [0,1] — accelerating (exponential-feel)
function curveY(t) {
  return 1 - Math.pow(t, 1.7);
}

function chartCoords(t) {
  const innerW = CHART_W - CHART_PAD_L - CHART_PAD_R;
  const innerH = CHART_H - CHART_PAD_T - CHART_PAD_B;
  const x = CHART_PAD_L + t * innerW;
  const y = CHART_PAD_T + curveY(t) * innerH;
  return { x, y };
}

function buildCurvePath() {
  const steps = 80;
  let d = "";
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const { x, y } = chartCoords(t);
    d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  }
  return d;
}

function GrowthChart() {
  const path = buildCurvePath();
  const baseLineY = CHART_H - CHART_PAD_B;

  // build a closed path for the area-fill under the curve
  const areaPath = (() => {
    const steps = 80;
    let d = "";
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const { x, y } = chartCoords(t);
      d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
    }
    const last = chartCoords(1);
    const first = chartCoords(0);
    d += ` L ${last.x} ${baseLineY} L ${first.x} ${baseLineY} Z`;
    return d;
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease, delay: 0.15 }}
      className="relative bg-white rounded-md ring-1 ring-neutral-200 shadow-[6px_6px_0_0_#0a0a0a] p-5"
      data-testid="growth-chart"
    >
      <div className="absolute -inset-3 border border-[var(--scout-blue)]/20 rounded-md pointer-events-none" />

      <div className="flex items-baseline justify-between mb-2">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400">
          ARR &middot; Customers &middot; Agent Footprint
        </div>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
          36-Month Outlook
        </div>
      </div>

      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="w-full h-auto block"
      >
        {/* Faint horizontal grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((g) => {
          const y = CHART_PAD_T + g * (CHART_H - CHART_PAD_T - CHART_PAD_B);
          return (
            <line
              key={g}
              x1={CHART_PAD_L}
              x2={CHART_W - CHART_PAD_R}
              y1={y}
              y2={y}
              stroke="#f1f5f9"
              strokeWidth="1"
            />
          );
        })}

        {/* Baseline */}
        <line
          x1={CHART_PAD_L}
          x2={CHART_W - CHART_PAD_R}
          y1={baseLineY}
          y2={baseLineY}
          stroke="#e5e7eb"
          strokeWidth="1.5"
        />

        {/* Area fill under the curve */}
        <defs>
          <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--scout-blue)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--scout-blue)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          d={areaPath}
          fill="url(#growthFill)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease, delay: 1.7 }}
        />

        {/* Main curve */}
        <motion.path
          d={path}
          fill="none"
          stroke="var(--scout-blue)"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.8, ease, delay: 0.4 }}
        />

        {/* Milestone drop lines */}
        {TRAJECTORY_MILESTONES.map((m, i) => {
          const { x, y } = chartCoords(m.t);
          return (
            <motion.line
              key={`drop-${m.label}`}
              x1={x}
              x2={x}
              y1={y}
              y2={baseLineY}
              stroke="var(--scout-blue)"
              strokeOpacity="0.25"
              strokeWidth="1"
              strokeDasharray="3 4"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{
                duration: 0.5,
                ease,
                delay: 1.6 + i * 0.15,
              }}
            />
          );
        })}

        {/* Milestone dots */}
        {TRAJECTORY_MILESTONES.map((m, i) => {
          const { x, y } = chartCoords(m.t);
          const isPast = m.isPast;
          const isCurrent = m.isCurrent;
          const ringColor = isCurrent
            ? "var(--scout-blue)"
            : isPast
            ? "#94a3b8"
            : "var(--scout-blue)";
          const ringOpacity = isPast ? 0.6 : 1;
          const fillCenter = isCurrent
            ? "var(--scout-blue)"
            : isPast
            ? "#94a3b8"
            : "var(--scout-blue)";
          const radius = isCurrent ? 12 : 9;
          return (
            <motion.g
              key={`dot-${m.label}`}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: ringOpacity, scale: 1 }}
              transition={{
                duration: 0.5,
                ease,
                delay: 1.5 + i * 0.15,
              }}
              style={{ transformOrigin: `${x}px ${y}px` }}
            >
              <circle cx={x} cy={y} r={radius} fill="white" />
              <circle
                cx={x}
                cy={y}
                r={radius}
                fill="none"
                stroke={ringColor}
                strokeWidth={isCurrent ? 4 : 3}
              />
              <circle
                cx={x}
                cy={y}
                r={isCurrent ? 5 : 3.5}
                fill={fillCenter}
              />

              {/* Past milestones: small check mark for "done" */}
              {isPast && (
                <path
                  d={`M ${x - 3} ${y} L ${x - 0.5} ${y + 2.5} L ${x + 3} ${y - 2.2}`}
                  fill="none"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* CURRENT — multi-ring pulse + glow */}
              {isCurrent && (
                <>
                  <motion.circle
                    cx={x}
                    cy={y}
                    r={12}
                    fill="none"
                    stroke="var(--scout-blue)"
                    strokeWidth="2"
                    initial={{ scale: 1, opacity: 0.7 }}
                    animate={{ scale: 3.4, opacity: 0 }}
                    transition={{
                      duration: 1.8,
                      ease: "easeOut",
                      delay: 2.4,
                      repeat: Infinity,
                      repeatDelay: 0.2,
                    }}
                    style={{ transformOrigin: `${x}px ${y}px` }}
                  />
                  <motion.circle
                    cx={x}
                    cy={y}
                    r={12}
                    fill="none"
                    stroke="var(--scout-blue)"
                    strokeWidth="2"
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 4.5, opacity: 0 }}
                    transition={{
                      duration: 2.2,
                      ease: "easeOut",
                      delay: 2.9,
                      repeat: Infinity,
                      repeatDelay: 0.2,
                    }}
                    style={{ transformOrigin: `${x}px ${y}px` }}
                  />
                  <motion.circle
                    cx={x}
                    cy={y}
                    r={12}
                    fill="var(--scout-blue)"
                    initial={{ opacity: 0.18 }}
                    animate={{ opacity: [0.18, 0.45, 0.18] }}
                    transition={{
                      duration: 1.6,
                      ease: "easeInOut",
                      delay: 2.4,
                      repeat: Infinity,
                    }}
                  />
                </>
              )}
            </motion.g>
          );
        })}

        {/* "TODAY" flag — big animated callout */}
        {(() => {
          const cur = TRAJECTORY_MILESTONES.find((m) => m.isCurrent);
          if (!cur) return null;
          const { x, y } = chartCoords(cur.t);
          const flagY = y - 46;
          return (
            <motion.g
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease, delay: 2.6 }}
            >
              {/* dotted vertical guide */}
              <motion.line
                x1={x}
                x2={x}
                y1={y - 18}
                y2={flagY + 14}
                stroke="var(--scout-blue)"
                strokeWidth="1.5"
                strokeDasharray="2 3"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, ease, delay: 2.7 }}
              />
              {/* flag bg */}
              <rect
                x={x - 38}
                y={flagY}
                width="76"
                height="22"
                rx="4"
                fill="var(--scout-blue)"
              />
              <motion.rect
                x={x - 38}
                y={flagY}
                width="76"
                height="22"
                rx="4"
                fill="none"
                stroke="var(--scout-blue)"
                strokeWidth="2"
                initial={{ scale: 1, opacity: 0.6 }}
                animate={{ scale: 1.18, opacity: 0 }}
                transition={{
                  duration: 1.4,
                  ease: "easeOut",
                  delay: 3.0,
                  repeat: Infinity,
                  repeatDelay: 0.4,
                }}
                style={{ transformOrigin: `${x}px ${flagY + 11}px` }}
              />
              <text
                x={x}
                y={flagY + 14}
                textAnchor="middle"
                fontFamily="Geist Mono, monospace"
                fontSize="10"
                fontWeight="700"
                letterSpacing="0.28em"
                fill="white"
              >
                TODAY
              </text>
            </motion.g>
          );
        })()}
      </svg>

      {/* Milestone labels strip below chart */}
      <div className="grid grid-cols-5 gap-1 mt-3">
        {TRAJECTORY_MILESTONES.map((m, i) => (
          <motion.div
            key={`label-${m.label}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 1.8 + i * 0.15 }}
            className={`flex flex-col gap-1 ${
              i === 0 ? "items-start" : i === 4 ? "items-end" : "items-center"
            }`}
          >
            <span
              className={`font-mono text-[9px] tracking-[0.22em] uppercase tabular-nums ${
                m.isCurrent
                  ? "text-[var(--scout-blue)]"
                  : m.isPast
                  ? "text-neutral-400"
                  : "text-neutral-400"
              }`}
            >
              {m.when}
            </span>
            <span
              className={`font-display text-[12px] font-medium leading-tight ${
                m.isCurrent
                  ? "text-[var(--scout-blue)]"
                  : m.isPast
                  ? "text-neutral-500 line-through decoration-neutral-300"
                  : "text-neutral-900"
              } ${i === 0 ? "text-left" : i === 4 ? "text-right" : "text-center"}`}
            >
              {m.label}
            </span>
            <ul
              className={`text-[10.5px] leading-snug space-y-0.5 ${
                m.isPast ? "text-neutral-400" : "text-neutral-500"
              } ${i === 0 ? "text-left" : i === 4 ? "text-right" : "text-center"}`}
            >
              {m.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

/* ---------- 12. The Ask ---------- */

const ASK_TOTAL = "$20M";

const ASK_CATEGORIES = [
  {
    name: "Enterprise Sales Expansion",
    amount: "$10M",
    pct: 50,
    details: [
      "Build out dedicated Broadcom channel team",
      "Build out dedicated AWS channel team (co-sell, Launcher, Marketplace)",
      "Direct EHR / Healthcare SMB sales team",
      "Enterprise AI sales leader + regional VPs",
      "Solution engineers across all 3 channels",
      "Enterprise account executives (DC · NYC · Boston pilot expansion)",
      "Customer success + sales engineering",
      "Conference presence + executive travel",
      "Sales enablement, demos, POC infrastructure",
    ],
  },
  {
    name: "Marketing + Category Creation",
    amount: "$5M",
    pct: 25,
    details: [
      "National digital advertising campaign",
      "Category creation — 'Governed Agentic Intelligence'",
      "Analyst engagement (Gartner, IDC, Forrester)",
      "Full-funnel inbound marketing engine",
      "Field marketing + conference strategy",
      "Content engine + thought leadership",
      "Enterprise AI education / certification",
      "Customer Advisory Board program scale-out",
      "Video, podcasts, and media production",
      "Co-branded campaigns with AWS + Broadcom",
    ],
  },
  {
    name: "Platform Engineering & AI Infrastructure",
    amount: "$3M",
    pct: 15,
    details: [
      "LockFox cryptographic infrastructure",
      "Critic Flock + Enforcer scale-out",
      "RPI / AI² scoring engine",
      "Observability + enterprise integrations",
      "Enterprise hardening, security, SOC 2 / FedRAMP path",
      "Agentic spacetime + scalability",
      "Agent Creator + marketplace builder",
      "Bedrock + multi-cloud infrastructure",
    ],
  },
  {
    name: "Executive & Key Personnel Compensation",
    amount: "$1.5M",
    pct: 7.5,
    details: [
      "Hire public-company-level CFO",
      "Tranche-based across 18-month runway",
      "Tied to revenue + channel milestones",
      "Key architect & scientist retention",
      "Critical AI leadership hires",
      "Scientific leadership (Promise Theory, swarm)",
    ],
  },
  {
    name: "Operations / Legal / Contingency",
    amount: "$500K",
    pct: 2.5,
    details: [
      "AI governance + compliance counsel",
      "Patent prosecution (7 patents pending)",
      "Enterprise contracts + procurement",
      "Audits (SOC 2, HIPAA, internal)",
      "Reserves for opportunistic moves",
    ],
  },
];

const ASK_PALETTE = [
  "var(--scout-blue)",
  "#3aa7d9",
  "#6dbde3",
  "#9bd1ec",
  "#c8e3f3",
];

function SlideAsk() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-ask"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Series A · What We&rsquo;re Raising
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
        {/* Hero amount + stacked allocation bar */}
        <div className="flex flex-col">
          <h1 className="font-display text-2xl md:text-3xl font-medium text-neutral-900 mb-2">
            The Ask
          </h1>

          <div className="font-display text-[120px] md:text-[180px] leading-none text-neutral-900 flex overflow-hidden mb-2">
            {ASK_TOTAL.split("").map((ch, i) => (
              <motion.span
                key={i}
                initial={{ y: "100%", opacity: 0 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 0.7, ease, delay: 0.15 + i * 0.08 }}
                className="inline-block"
              >
                {ch}
              </motion.span>
            ))}
          </div>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, ease, delay: 0.8 }}
            style={{ transformOrigin: "left" }}
            className="h-1 w-32 bg-[var(--scout-blue)] mb-6"
          />

          <p className="font-display text-base md:text-lg text-neutral-900 max-w-md mb-8 leading-snug">
            Aggressive Series A to fully attack the market through our
            three revenue channels (Broadcom, AWS, Direct EHR) and a
            national advertising push — scaling SCOUT into{" "}
            <span className="text-[var(--scout-blue)]">
              the governed operating layer for enterprise agentic workforces.
            </span>
          </p>

          {/* Stacked allocation bar */}
          <div className="mb-3 font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400">
            Allocation
          </div>
          <div className="h-3 w-full bg-neutral-100 rounded-full overflow-hidden flex">
            {ASK_CATEGORIES.map((c, i) => (
              <motion.span
                key={c.name}
                initial={{ width: 0 }}
                animate={{ width: `${c.pct}%` }}
                transition={{ duration: 1.0, ease, delay: 0.5 + i * 0.12 }}
                style={{ background: ASK_PALETTE[i] }}
                className="block h-full"
              />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
            {ASK_CATEGORIES.map((c, i) => (
              <div
                key={c.name}
                className="flex items-center gap-1.5 text-[11px] text-neutral-500"
              >
                <span
                  className="block h-2 w-2 rounded-sm"
                  style={{ background: ASK_PALETTE[i] }}
                />
                <span>{c.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: collapsible category list */}
        <div className="flex flex-col gap-3">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.45 }}
            className="flex items-center gap-3 mb-1"
          >
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)]">
              Click any line for the detail breakdown
            </span>
            <span className="block flex-1 h-px bg-[var(--scout-blue)]/30" />
            <motion.span
              animate={{ y: [0, 3, 0] }}
              transition={{ duration: 1.4, ease: "easeInOut", repeat: Infinity }}
              className="text-[var(--scout-blue)]"
            >
              <svg width="14" height="14" viewBox="0 0 14 14">
                <path
                  d="M3 5 L7 9 L11 5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.span>
          </motion.div>
          {ASK_CATEGORIES.map((c, i) => (
            <AskRow
              key={c.name}
              cat={c}
              colorIdx={i}
              delay={0.5 + i * 0.12}
              defaultOpen={i === 0}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AskRow({ cat, colorIdx, delay, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease, delay }}
      className={`border rounded-md overflow-hidden bg-white transition-colors ${
        open ? "border-[var(--scout-blue)]/60" : "border-neutral-200 hover:border-[var(--scout-blue)]/40"
      }`}
      data-testid={`ask-row-${colorIdx + 1}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-neutral-50 transition-colors cursor-pointer"
        aria-expanded={open}
      >
        <span
          className="block h-8 w-1 rounded-full shrink-0"
          style={{ background: ASK_PALETTE[colorIdx] }}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h3 className="font-display text-base md:text-lg font-medium text-neutral-900 leading-tight">
              {cat.name}
            </h3>
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 tabular-nums">
              {cat.pct}%
            </span>
          </div>
        </div>

        <div className="font-display text-xl md:text-2xl text-neutral-900 tabular-nums shrink-0">
          {cat.amount}
        </div>

        {/* Prominent expand pill */}
        <span
          className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] uppercase transition-all ${
            open
              ? "bg-[var(--scout-blue)] text-white"
              : "bg-neutral-100 text-neutral-700 group-hover:bg-[var(--scout-blue)]/10"
          }`}
        >
          {open ? "Hide" : "Details"}
          <svg
            width="11"
            height="11"
            viewBox="0 0 14 14"
            className={`transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          >
            <path
              d="M3 5 L7 9 L11 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <motion.div
        initial={false}
        animate={{
          height: open ? "auto" : 0,
          opacity: open ? 1 : 0,
        }}
        transition={{ duration: 0.35, ease }}
        className="overflow-hidden"
      >
        <div className="px-5 pb-5 pt-1 pl-10 border-t border-neutral-100">
          <ul className="space-y-1.5 text-[13px] text-neutral-700 leading-snug">
            {cat.details.map((d) => (
              <li key={d} className="flex items-start gap-2.5">
                <span
                  className="mt-[7px] block h-1.5 w-1.5 rounded-full shrink-0"
                  style={{ background: ASK_PALETTE[colorIdx] }}
                />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ---------- 12. Thank You ---------- */

function SlideThankYou() {
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center relative"
      data-testid="slide-thank-you"
    >
      <motion.div
        initial={{ opacity: 0, letterSpacing: "0.4em" }}
        animate={{ opacity: 1, letterSpacing: "-0.02em" }}
        transition={{ duration: 1.2, ease }}
        className="font-display text-6xl md:text-8xl text-neutral-900"
      >
        Thank you.
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease, delay: 0.8 }}
        className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-500"
      >
        getinearly@scoutagentics.com
      </motion.div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.0, ease, delay: 0.4 }}
        style={{ transformOrigin: "center" }}
        className="absolute bottom-24 h-px w-64 bg-[var(--scout-blue)]"
      />
    </div>
  );
}

/* ---------- 13. Mental Map (Finale) ---------- */

const MENTAL_MAP_IMG =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/934vgcp9_base%20blue%20video%20start.png";

const ENFORCER_CARD_IMG =
  "https://customer-assets.emergentagent.com/job_cb5e7d12-7ad6-4843-9ceb-1ecad227e318/artifacts/km3d46p1_afac7a3d-9561-477e-89f1-897f74dc7502.png";

function SlideMentalMap() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col"
      data-testid="slide-mental-map"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        World-First · Patented Agentic Spacetime™
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.05 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900"
      >
        We Map the <span className="text-[var(--scout-blue)]">Mind</span> of
        Every Agent
      </motion.h1>

      <div className="flex flex-col lg:flex-row gap-10 mt-8 items-start">
        {/* Mental Map visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease, delay: 0.2 }}
          className="lg:w-[58%] relative rounded-xl overflow-hidden ring-1 ring-neutral-200 shadow-[0_18px_50px_-16px_rgba(15,23,42,0.4)]"
          data-testid="mental-map-image"
        >
          <img
            src={MENTAL_MAP_IMG}
            alt="Scout Mental Map — Patented Agentic Spacetime"
            className="block w-full h-auto select-none"
            style={{ maxHeight: "62vh", objectFit: "cover", objectPosition: "top" }}
            draggable={false}
          />
          {/* North badge */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.7 }}
            className="absolute top-4 right-4 flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-sm"
            style={{ background: "rgba(14,116,144,0.92)" }}
          >
            <ArrowUp size={13} strokeWidth={2.5} className="text-white" />
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-white">
              North · On Track
            </span>
          </motion.div>
          {/* South badge */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease, delay: 0.85 }}
            className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full px-3 py-1.5 backdrop-blur-sm"
            style={{ background: "rgba(217,119,6,0.95)" }}
          >
            <ArrowDown size={13} strokeWidth={2.5} className="text-white" />
            <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-white">
              South · Dangerous Waters
            </span>
          </motion.div>
        </motion.div>

        {/* Narrative + Enforcer */}
        <div className="lg:w-[42%] flex flex-col gap-6">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.28 }}
            className="font-display text-xl md:text-2xl font-medium leading-snug text-neutral-900"
          >
            The first commercial product to{" "}
            <span className="text-[var(--scout-blue)]">
              map the brain of an agent.
            </span>
          </motion.p>

          <div className="flex flex-col gap-4">
            {[
              {
                icon: ArrowUp,
                accent: "#0e7490",
                title: "Thinking in the North",
                body: "Rewards, Satisfaction, Reinforcement — they're working exactly as they should.",
              },
              {
                icon: ArrowDown,
                accent: "#d97706",
                title: "Drifting to the South",
                body: "Sycophancy, Collusion, Self-Preservation — they're heading into dangerous waters.",
              },
              {
                icon: ShieldAlert,
                accent: "#dc2626",
                title: "The Enforcer is always watching",
                body: "Ready to pull them back on track. Or else.",
              },
            ].map((row, i) => {
              const Icon = row.icon;
              return (
                <motion.div
                  key={row.title}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease, delay: 0.4 + i * 0.12 }}
                  className="flex items-start gap-3"
                >
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center text-white shrink-0 mt-0.5"
                    style={{ background: row.accent }}
                  >
                    <Icon size={15} strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-display text-base font-medium text-neutral-900 leading-tight">
                      {row.title}
                    </div>
                    <p className="text-[13px] text-neutral-500 leading-relaxed mt-0.5">
                      {row.body}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease, delay: 0.85 }}
            className="rounded-xl overflow-hidden ring-1 ring-neutral-800/30 shadow-[0_14px_36px_-14px_rgba(15,23,42,0.5)] max-w-[240px]"
            data-testid="mental-map-enforcer"
          >
            <img
              src={ENFORCER_CARD_IMG}
              alt="The Enforcer — Rewards, Punishments, Terminations"
              className="block w-full h-auto select-none"
              draggable={false}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
}


/* ---------- Gartner Analysts ---------- */

const GARTNER_MARKET_GUIDE_IMG =
  "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/h9wjtxp9_image.png";

const GARTNER_ANALYSTS = [
  {
    name: "Cameron Haight",
    title: "VP Analyst",
    focus: "AI Vision, Strategy & Innovation",
    photo: "/gartner/cameron.jpg?v=3",
  },
  {
    name: "George Spafford",
    title: "VP Analyst",
    focus: "Platform Engineering & DevOps",
    photo: "/gartner/george.jpg?v=3",
  },
  {
    name: "Roger Williams",
    title: "Distinguished VP Analyst",
    focus: "Agentic SIMOPS",
    photo:
      "https://customer-assets.emergentagent.com/job_scout-seed-pitch/artifacts/os33a9yy_image.png",
  },
];

function SlideGartnerAnalysts() {
  return (
    <div
      className="w-full max-w-7xl flex flex-col items-start"
      data-testid="slide-gartner-analysts"
    >
      {/* Kicker */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease }}
        className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-4 flex items-center gap-3"
      >
        <span className="block w-8 h-px bg-[var(--scout-blue)]" />
        Analyst Validation
      </motion.div>

      {/* Title */}
      <motion.h1
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.08 }}
        className="font-display text-3xl md:text-4xl lg:text-5xl font-medium leading-[1.05] text-neutral-900 mb-3"
      >
        Validated by Gartner Analysts
      </motion.h1>

      {/* Shared headline */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease, delay: 0.18 }}
        className="text-base md:text-lg text-neutral-600 max-w-4xl leading-snug mb-10"
      >
        Scout was named in Gartner's 2026 Market Guide for Event
        Intelligence Solutions — vetted by three analysts who use Scout
        as their test bed for evaluating agentic platforms.
      </motion.p>

      <div className="w-full grid grid-cols-1 lg:grid-cols-[45fr_12fr_43fr] gap-6 items-start">
        {/* LEFT (45%) — Gartner Market Guide */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.25 }}
          className="relative"
        >
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3">
            <span className="block w-6 h-px bg-[var(--scout-blue)]" />
            Gartner Market Guide · 5 May 2026
          </div>
          <div className="relative">
            {/* Stacked depth layers behind */}
            <div
              className="absolute inset-0 rounded-md bg-[var(--scout-blue)]"
              style={{ transform: "translate(12px, 12px)", opacity: 0.18 }}
            />
            <div
              className="absolute inset-0 rounded-md bg-[var(--scout-blue)]"
              style={{ transform: "translate(6px, 6px)", opacity: 0.32 }}
            />
            {/* Image card */}
            <div
              className="relative rounded-md overflow-hidden bg-white"
              style={{
                boxShadow:
                  "10px 14px 26px -10px rgba(15,106,156,0.45), 4px 6px 0 0 #0a0a0a",
              }}
            >
              <img
                src={GARTNER_MARKET_GUIDE_IMG}
                alt="Gartner Market Guide for Event Intelligence Solutions — Scout listed"
                className="w-full h-auto block select-none"
                draggable={false}
                data-testid="gartner-market-guide-image"
              />
            </div>
          </div>
          <div className="mt-3 font-display text-sm md:text-base text-neutral-900">
            Event Intelligence Solutions — Scout Chosen Vendor
          </div>
        </motion.div>

        {/* CENTER (~10%) — 3 analysts stacked vertically */}
        <div className="flex flex-col items-center gap-7 py-2">
          <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-1 text-center">
            Analysts
          </div>
          {GARTNER_ANALYSTS.map((a, idx) => (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease, delay: 0.4 + idx * 0.1 }}
              className="flex flex-col items-center text-center"
              data-testid={`gartner-analyst-${a.name.toLowerCase().split(" ")[0]}`}
            >
              {/* Portrait */}
              <div className="relative">
                <div className="absolute -inset-1.5 rounded-full border border-[var(--scout-blue)]/25" />
                <div className="relative h-[88px] w-[88px] rounded-full overflow-hidden ring-2 ring-[var(--scout-blue)] bg-neutral-100">
                  <img
                    src={a.photo}
                    alt={a.name}
                    className="h-full w-full object-cover select-none"
                    draggable={false}
                  />
                </div>
              </div>
              <h2 className="mt-2.5 font-display text-[14px] font-medium leading-tight text-neutral-900">
                {a.name}
              </h2>
              <div className="mt-1 font-mono text-[8.5px] tracking-[0.18em] uppercase text-[var(--scout-blue)] leading-tight max-w-[14ch]">
                {a.title}
              </div>
              <p className="mt-1 text-[10.5px] text-neutral-600 leading-snug max-w-[16ch]">
                {a.focus}
              </p>
            </motion.div>
          ))}
        </div>

        {/* RIGHT (45%) — Competition matrix */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.35 }}
          className="flex flex-col"
        >
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--scout-blue)] mb-3 flex items-center gap-3">
            <span className="block w-6 h-px bg-[var(--scout-blue)]" />
            Competitive Position
          </div>
          <div className="w-full">
            <CompetitionMatrix />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ---------- Export deck ---------- */

export const SLIDES = [
  { title: "Cover", component: SlideCover },
  { title: "Problem", component: SlideProblem },
  { title: "The Agentic Population Explosion", component: SlideAgenticPopulation },
  { title: "Solution", component: SlideSolution },
  { title: "The Platform Implementation Pyramid", component: SlideProduct },
  { title: "The Mental Map", component: SlideMentalMap },
  { title: "Validated by Gartner Analysts", component: SlideGartnerAnalysts },
  // { title: "Business Model", component: SlideBusinessModel },
  // { title: "The Amazon Channel — AWS Goldmine", component: SlideAmazonChannel },
  { title: "The Broadcom Case Study — Coty Cosmetics", component: SlideBroadcomCoty },
  { title: "Purpose-Built Swarm Agents", component: SlideSwarmAgents },
  { title: "Phoebe — EHR & Medical Specialist", component: SlidePhoebe },
  { title: "The Harbinger — Risk & Compliance", component: SlideHarbinger },
  { title: "Ronald — Organizational Change", component: SlideRonald },
  { title: "Truman — Resource Strategy", component: SlideTruman },
  { title: "Holden — Cloud Strategy & Transformation", component: SlideHolden },
  { title: "Curt — Architecture", component: SlideCurt },
  { title: "Wendy — Reporting & Analytics", component: SlideWendy },
  { title: "Jules — PMO & Product Management", component: SlideJules },
  { title: "Sitterson — FinOps", component: SlideSitterson },
  { title: "Dana — Site Reliability Engineering", component: SlideDana },
  { title: "Hadley — Reliability Engineering", component: SlideHadley },
  { title: "Marty — Product Lifecycle", component: SlideMarty },
  { title: "The Director — Platform Governance", component: SlideDirector },
  { title: "Bishop — EIS Gateway", component: SlideBishop },
  { title: "The Trender — EIS Pattern Cartographer", component: SlideTrender },
  { title: "The Predictor — EIS Improvement Quantifier", component: SlidePredictor },
  { title: "The Drifter — EIS Statistical Drift Detection", component: SlideDrifter },
  { title: "The Prophet — EIS Cross-Cloud Network Monitor", component: SlideProphet },
  { title: "The Blender — EIS Company-Wide Alarm Correlation", component: SlideBlender },
  { title: "The Critic — EIS Agentic Governance Almighty", component: SlideCritic },
  // { title: "Market", component: SlideMarket },
  // { title: "Traction", component: SlideTraction },
  // { title: "Competition", component: SlideCompetition },
  // { title: "Go-to-Market", component: SlideGTM },
  // { title: "Growth Trajectory", component: SlideGrowth },
  // { title: "The Ask", component: SlideAsk },
  // { title: "Thank You", component: SlideThankYou },
];
